import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, Save, Loader2, AlertCircle, ArrowLeft, Users, Mail, Phone, MapPin, Briefcase, LayoutTemplate, Send, MessageSquare, Plus, FileText, Trash2, Upload, File, Calendar, CheckCircle2, Wrench, ChevronRight, X, Edit, ExternalLink, Search, Bot, AlignLeft, Globe, Landmark } from 'lucide-react';
import { supabase, supabase_get_client_communications, supabase_save_client_communication, supabase_upload_file, supabase_delete_document, supabase_get_client_shortlist_bundle, supabase_get_flows, supabase_get_templates, supabase_update_application_status, supabase_log_interaction } from '../services/supabaseService';
import { CandidatePipelineStrip, CandidateShortlistPhasesPanel, ClientVacancyListTable } from './talentPool/CandidateShortlistJourney';
import { filterClientShortlistBundle, filterClientVacanciesList } from '../utils/clientShortlistSearch';
import { generateIndustryName } from '../services/geminiService';
import { User, UserRole, AutomationTemplate } from '../types';
import AiRichTextEditor from './AiRichTextEditor';
import SearchableCombobox from './SearchableCombobox';
import CandidateMoveModal from './CandidateMoveModal';
import MergeClientModal from './MergeClientModal';
import { ArrowRightLeft } from 'lucide-react';
import { triggerAutomation } from '../services/automationService';
import { DEFAULT_KANBAN_STAGES, WERFTOOLS_DEFAULT_TENANT_CLIENT_ID } from '../constants';
import { getCandidateEmail } from '../utils/candidateContact';
import {
    pipelineStepToShortlistPhase,
    type PipelineStepId,
    type ShortlistPhaseId,
} from '../utils/candidateJourneyBuckets';

const CRM_SHORTLIST_ORG_SESSION_KEY = 'wt:crm_shortlist_org_client_id';
const CRM_SHORTLIST_STATUS_FOCUS_KEY = 'wt:crm_shortlist_status_focus';

function crmContactLinkedOrgId(row: { client_id?: string | null; linked_client_id?: string | null } | null | undefined): string | undefined {
    if (!row) return undefined;
    const linked = String(row.linked_client_id || '').trim();
    if (linked) return linked;
    const cid = String(row.client_id || '').trim();
    if (cid && cid !== WERFTOOLS_DEFAULT_TENANT_CLIENT_ID) return cid;
    return undefined;
}

/** Ensure the org being viewed can be selected even if it is not returned by the bulk `clients` query (e.g. RLS). */
function buildCRMClientComboboxOptions(
    availableCompanies: any[],
    entityType: 'company' | 'contact',
    entity: any | null | undefined
): { id: string; title: string }[] {
    const base = (availableCompanies || []).map((c) => ({ id: c.id, title: c.name || 'Naamloos' }));
    if (entityType === 'company' && entity?.id) {
        const id = String(entity.id);
        if (!base.some((o) => o.id === id)) {
            const title =
                entity.name ||
                (typeof entity.data === 'object' && entity.data?.name) ||
                entity.data?.companyName ||
                'Huidig bedrijf';
            return [{ id, title }, ...base];
        }
    }
    return base;
}

function contactLinkedCompany(entityRow: any) {
    return entityRow?.linked_org || entityRow?.clients;
}

interface CRMDashboardProps {
    entity: any;
    entityType: 'company' | 'contact';
    onBack: () => void;
    onSaveSuccess: () => void;
    user: User;
    onNavigateVacancy?: (vacancyId: string) => void;
    onNavigateCandidate?: (candidateId: string) => void;
    onLoginAs?: (clientId: string, clientName: string, portalTestEmail?: string) => void;
    /** Sluit CRM-detail en spring naar tab Bedrijven (nieuw contact → eerst bedrijf). */
    onNavigateToCompanies?: () => void;
    /** Global shortlist met CRM-org filter (sessionStorage). */
    onNavigateToShortlist?: () => void;
}

type CompanyTab = 'Overzicht' | 'Communicatie' | 'Contactpersonen' | 'Bestanden' | 'Shortlist' | 'Vacatures' | 'Automation';
type ContactTab = 'Algemeen' | 'Contact Center' | 'Shortlist' | 'Vacatures' | 'Clientportaal';

const CRMDashboard: React.FC<CRMDashboardProps> = ({
    entity,
    entityType,
    onBack,
    onSaveSuccess,
    user,
    onNavigateVacancy,
    onNavigateCandidate,
    onLoginAs,
    onNavigateToCompanies,
    onNavigateToShortlist,
}) => {
    const isNew = !entity;
    const [isEditing, setIsEditing] = useState(isNew);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [activeCompanyTab, setActiveCompanyTab] = useState<CompanyTab>('Overzicht');
    const [activeContactTab, setActiveContactTab] = useState<ContactTab>('Algemeen');

    // QUICK SCRIPT: Force sync pipelines
    useEffect(() => {
        if (!user || !user.clientId) return;
        const forceSyncPipelines = async () => {
            const syncKey = 'pipeline_resync_v6';
            if (localStorage.getItem(syncKey)) return;
            
            try {
                console.log('--- START BACKGROUND PIPELINE SYNC ---');
                const bhTokens = localStorage.getItem('bullhorn_tokens');
                if (!bhTokens) return;
                const auth = JSON.parse(bhTokens);
                
                const { supabase_sync_bullhorn_job_submissions } = await import('../services/supabaseService');
                const { fetchBullhornJobSubmissions } = await import('../services/bullhornService');
                const { supabase } = await import('../services/supabaseService');

                const { data: vacs } = await supabase.from('vacancies').select('id, bullhorn_id, data').eq('client_id', user.clientId);
                if (!vacs) return;

                console.log('Found ' + vacs.length + ' vacancies to sync candidates for!');
                for (const v of vacs) {
                    if (!v.bullhorn_id) continue;
                    try {
                        console.log('Fetching subs for job ' + v.bullhorn_id);
                        const subs = await fetchBullhornJobSubmissions(auth, v.bullhorn_id);
                        if (subs && subs.length > 0) {
                            console.log('Syncing ' + subs.length + ' candidates for ' + v.bullhorn_id);
                            await supabase_sync_bullhorn_job_submissions(user.clientId, v.id, subs);
                        }
                    } catch(e) {
                         console.error('Error on vac', v.bullhorn_id, e);
                    }
                }
                localStorage.setItem(syncKey, 'done');
                console.log('--- DONE BACKGROUND PIPELINE SYNC ---');
                window.location.reload();
            } catch(err) {
                console.error(err);
            }
        };
        forceSyncPipelines();
    }, [user]);

    const [formData, setFormData] = useState<any>({});
    const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
    const [companiesLoadError, setCompaniesLoadError] = useState<string | null>(null);

    // Delete Confirmation State
    const [deleteConfirmInfo, setDeleteConfirmInfo] = useState<{type: 'main' | 'contact', id?: string} | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Communication State
    const [communications, setCommunications] = useState<any[]>([]);
    const [newCommText, setNewCommText] = useState('');
    const [commType, setCommType] = useState<'note' | 'email' | 'call' | 'whatsapp'>('note');
    const [isSendingComm, setIsSendingComm] = useState(false);
    const commScrollRef = React.useRef<HTMLDivElement>(null);

    // Context / Related Entities State
    const [companyContacts, setCompanyContacts] = useState<any[]>([]);
    const [isLoadingContacts, setIsLoadingContacts] = useState(false);

    // Contact Details Drawer State
    const [selectedContact, setSelectedContact] = useState<any>(null);
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [contactForm, setContactForm] = useState<any>({});
    const [isSavingContact, setIsSavingContact] = useState(false);
    
    // Document State
    const [companyDocuments, setCompanyDocuments] = useState<any[]>([]);
    const [isLoadingDocs, setIsLoadingDocs] = useState(false);

    // DNS Checker State
    const [isCheckingDns, setIsCheckingDns] = useState(false);
    const [dnsStatus, setDnsStatus] = useState<'success' | 'error' | null>(null);
    const [dnsMessage, setDnsMessage] = useState('');

    // Shortlist State (bedrijf/contact — zelfde journey als Talentpool)
    const [clientApplications, setClientApplications] = useState<any[]>([]);
    const [clientInterviews, setClientInterviews] = useState<any[]>([]);
    const [clientPlacements, setClientPlacements] = useState<any[]>([]);
    const [clientStageHistory, setClientStageHistory] = useState<any[]>([]);
    const [shortlistScope, setShortlistScope] = useState<'current' | 'all'>('all');
    const [crmShortlistFocusPhase, setCrmShortlistFocusPhase] = useState<ShortlistPhaseId | null>(null);
    const [crmShortlistFocusKey, setCrmShortlistFocusKey] = useState(0);
    const [isLoadingApps, setIsLoadingApps] = useState(false);

    // Automation State
    const [automationFlows, setAutomationFlows] = useState<any[]>([]);
    const [isLoadingFlows, setIsLoadingFlows] = useState(false);

    // Latest Vacancies State
    const [latestVacancies, setLatestVacancies] = useState<any[]>([]);
    const [isLoadingVacancies, setIsLoadingVacancies] = useState(false);

    // Client Portal State
    const [portalInvites, setPortalInvites] = useState<any[]>([]);
    const [isLoadingInvites, setIsLoadingInvites] = useState(false);
    const [newInviteEmail, setNewInviteEmail] = useState('');

    const [vacanciesSearch, setVacanciesSearch] = useState('');
    const [shortlistSearch, setShortlistSearch] = useState('');

    // Call Feature State
    const [recruiterPhone, setRecruiterPhone] = useState((user as any)?.phone || '');
    const [isCalling, setIsCalling] = useState(false);

    // KVK State
    const [isSearchingKvk, setIsSearchingKvk] = useState(false);
    const [isGeneratingIndustry, setIsGeneratingIndustry] = useState(false);
    const [kvkResults, setKvkResults] = useState<any[]>([]);
    const [kvkSearchedEmpty, setKvkSearchedEmpty] = useState<string | null>(null);
    // AI Logo Search
    const [showLogoSearch, setShowLogoSearch] = useState(false);
    const [logoOptions, setLogoOptions] = useState<any[]>([]);
    const [isSearchingLogo, setIsSearchingLogo] = useState(false);
    const [logoSearchError, setLogoSearchError] = useState('');

    // Partner Portal State
    const [partnerDomain, setPartnerDomain] = useState<string | null>(null);

    // Candidate Move & Merge Modal State
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [moveDetails, setMoveDetails] = useState<any>(null);
    const [templates, setTemplates] = useState<AutomationTemplate[]>([]);
    const [rejectionReasonsList, setRejectionReasonsList] = useState<string[]>(['Kandidaat teruggetrokken', 'Afgewezen door opdrachtgever', 'Opdracht ingetrokken', 'Overig']);

    useEffect(() => {
        const loadTemplatesAndSettings = async () => {
            try {
                const temps = await supabase_get_templates();
                setTemplates(temps || []);

                if (user?.clientId) {
                    const { data: clientData } = await supabase.from('clients').select('data').eq('id', user.clientId).single();
                    if (clientData?.data?.ai_matching_settings?.rejectionReasons) {
                        setRejectionReasonsList(clientData.data.ai_matching_settings.rejectionReasons);
                    }
                }
            } catch (e) {
                console.error(e);
            }
        };
        loadTemplatesAndSettings();
    }, [user.id, user.clientId]);

    const handleConfirmShortlistMove = async (channel: 'email' | 'whatsapp' | 'log', templateId: string, subject: string, messageContent: string, internalNoteContent: string, targetStage?: string, asDraft?: boolean, rejectionReason?: string, cc?: string, bcc?: string, fromEmail?: string, _voorgesteld?: import('../utils/portalProposalThread').VoorgesteldClientPayload | null) => {
        if (!moveDetails) return;
        try {
            const finalInternalNote = rejectionReason ? `${internalNoteContent || ''}\n\nAfwijzingsreden: ${rejectionReason}`.trim() : internalNoteContent;
            const finalTargetStage = targetStage || moveDetails.toStage;
            const targetStageObj = DEFAULT_KANBAN_STAGES.find(s => s.name === finalTargetStage || s.id === finalTargetStage) || { id: finalTargetStage, name: finalTargetStage };

            // 1. Update Application Status
            await supabase_update_application_status(moveDetails.candidateId || moveDetails.applicationId, targetStageObj.id);

            // 2. Trigger Automation
            const autoPayload = {
                vacancyId: moveDetails.vacancyId,
                candidateId: moveDetails.candidateRealId,
                stageId: targetStageObj.id,
                stageName: targetStageObj.name,
                user_id: user.id
            };
            await triggerAutomation('stage_changed', autoPayload);

            // 3. Communications
            if (channel !== 'log') {
                await supabase.from('todos').insert({
                    title: `[OUTBOX] Bericht aan ${moveDetails.candidateName}`,
                    description: messageContent,
                    status: 'Pending',
                    due_date: new Date().toISOString(),
                    user_id: user.id,
                    assignee_id: user.id
                });
                alert('Bericht is opgeslagen in je Postvak UIT (Recruiter Hub).');
            }

            const nameStr = (user as any).profile?.name || user.name || 'Recruiter';
            const clientId = entityType === 'company' ? entity?.id : (crmContactLinkedOrgId(entity) || entity?.id);
            if (clientId) {
                // Log system movement
                await supabase_log_interaction(
                    moveDetails.candidateRealId,
                    clientId,
                    'system',
                    `(Status Update) ${nameStr} heeft kandidaat verplaatst naar '${finalTargetStage}'.${rejectionReason ? ` Reden: ${rejectionReason}` : ''}`
                );

                if (finalInternalNote && finalInternalNote.trim()) {
                    await supabase_log_interaction(
                        moveDetails.candidateRealId,
                        clientId,
                        'note',
                        `[Verplaatst naar ${finalTargetStage}] ${finalInternalNote}`
                    );
                }

                if (channel !== 'log' && messageContent) {
                    const logType = channel === 'whatsapp' ? 'whatsapp' : 'email';
                    await supabase_log_interaction(
                        moveDetails.candidateRealId,
                        clientId,
                        logType,
                        `[Inkomend/Uitgaand: ${finalTargetStage}] ${messageContent}`
                    );
                }

                if (rejectionReason) {
                    const { data: profile } = await supabase.from('candidates').select('ai_parsed_data').eq('id', moveDetails.candidateRealId).eq('client_id', clientId).single();
                    if (profile) {
                        const existingData = profile.ai_parsed_data || {};
                        await supabase.from('candidates').update({
                            ai_parsed_data: { ...existingData, rejectionReason: rejectionReason }
                        }).eq('id', moveDetails.candidateRealId).eq('client_id', clientId);
                    }
                }
            }

            setShowMoveModal(false);
            setMoveDetails(null);
            await loadClientShortlistBundle();
            alert('Status succesvol aangepast!');
        } catch (e: any) {
            console.error("Move failed", e);
            alert("Er ging iets mis bij het verplaatsen.");
        }
    };

    useEffect(() => {
        if (!formData.is_partner) {
            // For companies, look at parent_client_id. For contacts, look at their linked company's parent_client_id if available, otherwise fallback
            let partnerId = user?.clientId;
            if (entityType === 'company') {
                partnerId = formData.parent_client_id || partnerId;
            } else if (entityType === 'contact') {
                partnerId = formData.linked_org?.parent_client_id || formData.clients?.parent_client_id || partnerId;
            }

            if (!partnerId) return;

            const fetchPartnerDomain = async () => {
                try {
                    const { data } = await supabase.from('clients')
                        .select('clientportal_custom_domain')
                        .eq('id', partnerId)
                        .single();
                    if (data?.clientportal_custom_domain) {
                        setPartnerDomain(data.clientportal_custom_domain);
                    }
                } catch (e) { console.error(e); }
            };
            fetchPartnerDomain();
        }
    }, [user?.clientId, entityType, formData.is_partner, formData.parent_client_id]);

    const handleSearchKvk = async () => {
        if (!formData.name) return;
        setIsSearchingKvk(true);
        setKvkSearchedEmpty(null);
        try {
            const res = await fetch(`${(import.meta as any).env.VITE_SERVER_URL || ''}/api/kvk/search?query=${encodeURIComponent(formData.name)}`);
            const text = await res.text();
            try {
                const data = JSON.parse(text);
                if (res.status === 500 || data.error) {
                    setKvkResults([]);
                    const errStr = data.details ? String(data.details) : '';
                    if (errStr.includes("401") || errStr.includes("Unauthorized")) {
                        setKvkSearchedEmpty("Je KVK API key heeft geen rechten geselecteerd voor de 'Zoeken API'. Voeg deze toe in je developer.kvk.nl portaal!");
                    } else {
                        setKvkSearchedEmpty(`KVK Fout: ${data.error || 'Server error'}`);
                    }
                } else if (data.resultaten && data.resultaten.length > 0) {
                    setKvkResults(data.resultaten);
                    setKvkSearchedEmpty(null);
                } else {
                    setKvkResults([]);
                    setKvkSearchedEmpty("Geen KVK resultaten gevonden voor deze zoekopdracht.");
                }
            } catch(e) {
                console.error("KVK API returned non-JSON:", text.substring(0, 100));
                setKvkSearchedEmpty("De KVK server lijkt momenteel niet bereikbaar over het netwerk.");
                setKvkResults([]);
            }
        } catch (e) {
            console.error(e);
            setKvkSearchedEmpty("KVK zoeken mislukt door een netwerkfout.");
            setKvkResults([]);
        } finally {
            setIsSearchingKvk(false);
        }
    };

    const handleSelectKvkResult = async (result: any) => {
        const prev = formData;
        setKvkResults([]);
        setKvkSearchedEmpty(null);
        
        let newWebsite = prev.website;
        let newPhone = prev.phone;
        let newEmail = prev.email;
        let newIndustry = prev.industry;
        let kvkExtraData: any = {};
        
        if (result.kvkNummer) {
            try {
                const res = await fetch(`${(import.meta as any).env.VITE_SERVER_URL || ''}/api/kvk/profile?kvkNumber=${result.kvkNummer}`);
                if (res.ok) {
                    const profileStr = await res.text();
                    try {
                        const profileData = JSON.parse(profileStr);
                        if (profileData && !profileData.error) {
                            kvkExtraData.rsin = profileData._embedded?.hoofdvestiging?.rsin || profileData._embedded?.eigenaar?.rsin || '';
                            kvkExtraData.rechtsvorm = profileData._embedded?.eigenaar?.rechtsvorm || '';
                            
                            const sbiArr = profileData.sbiActiviteiten || [];
                            kvkExtraData.sbi = sbiArr.map((a:any) => a.sbiOmschrijving).join(', ');
                            if (sbiArr.length > 0 && !newIndustry) newIndustry = sbiArr[0].sbiOmschrijving;
                            
                            const websites = profileData._embedded?.hoofdvestiging?.websites || [];
                            if (websites.length > 0 && !newWebsite) newWebsite = websites[0];
                            
                            const phones = profileData._embedded?.hoofdvestiging?.telefoonnummers || [];
                            if (phones.length > 0 && !newPhone) newPhone = phones[0];
                            
                            const emails = profileData._embedded?.hoofdvestiging?.emailadressen || [];
                            if (emails.length > 0 && !newEmail) newEmail = emails[0];
                        }
                    } catch(e) {}
                }
            } catch(e) {}
        }
        
        const finalName = result.naam || result.handelsnaam || prev.name;
        
        if (newIndustry && kvkExtraData.sbi) {
            setIsGeneratingIndustry(true);
            generateIndustryName(finalName, newIndustry).then(aiIndustry => {
                if (aiIndustry && aiIndustry !== newIndustry) {
                    setFormData((pf: any) => ({...pf, industry: aiIndustry}));
                }
            }).catch(e => console.error(e)).finally(() => setIsGeneratingIndustry(false));
        }

        setFormData({
            ...prev,
            name: finalName,
            industry: newIndustry || prev.industry,
            website: newWebsite,
            phone: newPhone,
            email: newEmail,
            address_street: (result.adres?.binnenlandsAdres?.straatnaam ? `${result.adres.binnenlandsAdres.straatnaam} ${result.adres.binnenlandsAdres.huisnummer||''}${result.adres.binnenlandsAdres.huisnummerToevoeging||''}`.trim() : (result.straatnaam ? `${result.straatnaam} ${result.huisnummer||''}${result.huisnummerToevoeging||''}`.trim() : prev.address_street)),
            address_zip: result.adres?.binnenlandsAdres?.postcode || result.postcode || prev.address_zip,
            address_city: result.adres?.binnenlandsAdres?.plaats || result.plaats || prev.address_city,
            invoice_kvk: result.kvkNummer || prev.invoice_kvk,
            data: { ...(prev.data || {}), ...kvkExtraData }
        });
    };

    const convertImageToPng = async (fileOrBlob: File | Blob): Promise<Blob> => {
        if (!fileOrBlob.type.includes('webp') && !fileOrBlob.type.includes('avif')) {
            return fileOrBlob;
        }
        try {
            const bitmap = await createImageBitmap(fileOrBlob);
            const canvas = document.createElement('canvas');
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return fileOrBlob;
            ctx.drawImage(bitmap, 0, 0);
            const pngBlob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/png'));
            return pngBlob || fileOrBlob;
        } catch (e) {
            console.warn("Could not convert image format", e);
            return fileOrBlob;
        }
    };

    const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        let fileRaw = event.target.files?.[0];
        if (!fileRaw || !entity) return;
        setIsProcessing(true);
        try {
            const convertedBlob = await convertImageToPng(fileRaw);
            const file = new window.File([convertedBlob], `${entity.id}-logo.png`, { type: convertedBlob.type || 'image/png' });
            
            const uploadedUrl = await supabase_upload_file(file, 'documents', 'logos/');
            if (uploadedUrl) {
                setFormData({ ...formData, logo_url: uploadedUrl });
                if (!isNew) {
                    await supabase.from('clients').update({ logo_url: uploadedUrl }).eq('id', entity.id);
                }
            }
        } catch (error) {
            console.error(error);
            alert("Logo uploaden mislukt.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSearchLogo = async () => {
        if (!formData.name) return;
        setIsSearchingLogo(true);
        setLogoSearchError('');
        console.log(`[ZOEK LOGO] Knop ingedrukt voor bedrijf: "${formData.name}"`);
        console.log(`[ZOEK LOGO] Verzenden aanvraag met clientID: ${user.clientId || 'GEEN CLIENT ID'}`);
        try {
            const domain = formData.website ? formData.website.replace(/^https?:\/\//, '').replace(/\/.*$/, '') : '';
            const url = `${(import.meta as any).env.VITE_SERVER_URL || ''}/api/logo-search?q=${encodeURIComponent(formData.name)}&clientId=${user.clientId || ''}&website=${encodeURIComponent(domain)}`;
            console.log(`[ZOEK LOGO] Backend URL:`, url);
            const res = await fetch(url);
            console.log(`[ZOEK LOGO] Backend response status:`, res.status);
            if (!res.ok) throw new Error(`API error HTTP ${res.status}`);
            const data = await res.json();
            console.log(`[ZOEK LOGO] Succesvol JSON ontvangen van backend. Aantal afbeeldingen:`, data.images?.length || 0);
            setLogoOptions(data.images || []);
            if (!data.images || data.images.length === 0) setLogoSearchError('Geen logo\'s gevonden. Probeer een andere bedrijfsnaam.');
        } catch (e) {
            console.error(`[ZOEK LOGO] Fout opgetreden tijdens ophalen:`, e);
            setLogoSearchError('Kan geen logo\'s ophalen. Controleer de backend verbinding.');
            setLogoOptions([]);
        } finally {
            setIsSearchingLogo(false);
        }
    };

    useEffect(() => {
        if (showLogoSearch && logoOptions.length === 0 && !isSearchingLogo && !logoSearchError) {
            handleSearchLogo();
        }
    }, [showLogoSearch]);

    const handleSelectAILogo = async (imageUrl: string) => {
        if (!entity || !imageUrl) return;
        setIsProcessing(true);
        setShowLogoSearch(false);
        try {
            let finalUrl = imageUrl;
            try {
                const proxyUrl = `${(import.meta as any).env.VITE_SERVER_URL || ''}/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
                const imgRes = await fetch(proxyUrl);
                const rawBlob = await imgRes.blob();
                const convertedBlob = await convertImageToPng(rawBlob);
                const file = new window.File([convertedBlob], `${entity.id}-logo.png`, { type: convertedBlob.type || 'image/png' });
                const uploadedUrl = await supabase_upload_file(file, 'documents', 'logos/');
                if (uploadedUrl) finalUrl = uploadedUrl;
            } catch(corsError) {
                console.warn('CORS download fallback to raw external url', corsError);
            }
            setFormData({ ...formData, logo_url: finalUrl });
            if (!isNew) {
                await supabase.from('clients').update({ logo_url: finalUrl }).eq('id', entity.id);
            }
        } catch (error) {
            console.error(error);
            alert("Toepassen logo mislukt.");
        } finally {
            setIsProcessing(false);
        }
    };

    const loadCommunications = async () => {
        if (!entity || entityType !== 'company') return;
        const comms = await supabase_get_client_communications(entity.id);
        setCommunications(comms);
    };

    const loadCompanyContacts = async () => {
        if (!entity || entityType !== 'company') return;
        setIsLoadingContacts(true);
        try {
            const { data } = await supabase.from('client_contacts').select('*').eq('linked_client_id', entity.id).order('first_name');
            setCompanyContacts(data || []);
        } catch (e) { console.error(e); } finally { setIsLoadingContacts(false); }
    };

    const loadCompanyDocuments = async () => {
        if (!entity || entityType !== 'company') return;
        setIsLoadingDocs(true);
        try {
            const { data } = await supabase.from('documents').select('*').eq('client_id', entity.id).eq('is_template', false).is('candidate_id', null).order('created_at', { ascending: false });
            setCompanyDocuments(data || []);
        } catch (e) { console.error(e); } finally { setIsLoadingDocs(false); }
    };

    const loadClientShortlistBundle = useCallback(async () => {
        if (!entity) return;
        const targetId = entityType === 'company' ? entity.id : crmContactLinkedOrgId(entity);
        if (!targetId) return;
        setIsLoadingApps(true);
        try {
            const bundle = await supabase_get_client_shortlist_bundle(targetId);
            setClientApplications(bundle.applications);
            setClientInterviews(bundle.interviews);
            setClientPlacements(bundle.placements);
            setClientStageHistory(bundle.stageHistory);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoadingApps(false);
        }
    }, [entity, entityType]);

    const crmShortlistCount = useMemo(() => clientApplications.length, [clientApplications]);

    const resolveCrmVacancyClient = useCallback(
        (clientId: string | null | undefined, embed: any) => {
            const row = Array.isArray(embed) ? embed[0] : embed;
            if (row?.name || row?.logo_url) return row;
            const id = String(clientId || row?.id || '').trim();
            if (!id) return null;
            const c = availableCompanies.find((x) => x.id === id);
            if (c) return { name: c.name, logo_url: c.logo_url };
            if (entityType === 'company' && id === entity?.id) {
                return { name: formData.name, logo_url: formData.logo_url };
            }
            return null;
        },
        [availableCompanies, entityType, entity?.id, formData.name, formData.logo_url]
    );

    const filteredCrmVacancies = useMemo(
        () =>
            filterClientVacanciesList(latestVacancies, vacanciesSearch, resolveCrmVacancyClient).map((v: any) => ({
                ...v,
                location: v.location || v.data?.location || '—',
            })),
        [latestVacancies, vacanciesSearch, resolveCrmVacancyClient]
    );

    const filteredCrmShortlistBundle = useMemo(
        () =>
            filterClientShortlistBundle(
                shortlistSearch,
                clientApplications,
                clientInterviews,
                clientPlacements,
                clientStageHistory
            ),
        [shortlistSearch, clientApplications, clientInterviews, clientPlacements, clientStageHistory]
    );

    const openCrmShortlistMoveForApplication = useCallback(
        (app: any) => {
            const vac = app.vacancies;
            const cand = app.candidates;
            setMoveDetails({
                applicationId: app.id,
                candidateId: app.candidate_id,
                candidateRealId: app.candidate_id,
                candidateName: cand?.name || 'Kandidaat',
                candidateEmail: getCandidateEmail(cand),
                fromStage: app.status || 'Shortlist',
                toStage: app.status || 'Shortlist',
                vacancyId: app.vacancy_id,
                vacancyTitle: vac?.title || '',
                clientName:
                    vac?.eindklant?.name ||
                    vac?.opdrachtgever?.name ||
                    entity?.name ||
                    '',
            });
            setShowMoveModal(true);
        },
        [entity?.name]
    );

    const showCrmShortlistJourney = !isNew && (entityType === 'company' || !!crmContactLinkedOrgId(entity));

    useEffect(() => {
        if (!showCrmShortlistJourney) return;
        void loadClientShortlistBundle();
    }, [showCrmShortlistJourney, loadClientShortlistBundle]);

    const loadAutomationFlows = async () => {
        if (!entity) return;
        const targetId = entityType === 'company' ? entity.id : crmContactLinkedOrgId(entity);
        if (!targetId) return;
        setIsLoadingFlows(true);
        try {
            const flows = await supabase_get_flows(targetId);
            setAutomationFlows(flows);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoadingFlows(false);
        }
    };

    const loadLatestVacancies = async () => {
        if (!entity) return;
        const targetId = entityType === 'company' ? entity.id : crmContactLinkedOrgId(entity);
        if (!targetId) return;
        setIsLoadingVacancies(true);
        try {
            const { data, error } = await supabase
                .from('vacancies')
                .select(
                    `id, title, status, vacancy_number, created_at, data, opdrachtgever_id, eindklant_id, client_id,
                    opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
                    eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)`
                )
                .or(`client_id.eq.${targetId},opdrachtgever_id.eq.${targetId},eindklant_id.eq.${targetId}`)
                .order('created_at', { ascending: false });
            if (error) throw error;
            setLatestVacancies(data || []);
        } catch (e) { console.error(e); } finally { setIsLoadingVacancies(false); }
    };

    const loadPortalInvites = async (forceClientId?: string) => {
        const targetClientId = forceClientId || (entityType === 'contact' ? crmContactLinkedOrgId(entity) : undefined);
        if (!targetClientId) return;
        
        setIsLoadingInvites(true);
        try {
            const { data, error } = await supabase
                .from('client_portal_invites')
                .select('*')
                .eq('client_id', targetClientId)
                .order('created_at', { ascending: false });
            if (error) throw error;
            setPortalInvites(data || []);
        } catch (e) {
            console.error("Portal invites fetch failed:", e);
        } finally {
            setIsLoadingInvites(false);
        }
    };

    useEffect(() => {
        if (!entity) return;
        // Company
        if (entityType === 'company') {
            if (activeCompanyTab === 'Communicatie') loadCommunications();
            if (activeCompanyTab === 'Contactpersonen') loadCompanyContacts();
            if (activeCompanyTab === 'Bestanden') loadCompanyDocuments();
            if (activeCompanyTab === 'Shortlist') loadClientShortlistBundle();
            if (activeCompanyTab === 'Vacatures') loadLatestVacancies();
            if (activeCompanyTab === 'Automation') loadAutomationFlows();
            
            if (activeCompanyTab === 'Overzicht') {
                loadLatestVacancies();
                loadClientShortlistBundle();
            }
        } else {
            // Contact
            if (activeContactTab === 'Shortlist') loadClientShortlistBundle();
            if (activeContactTab === 'Vacatures') loadLatestVacancies();
            if (activeContactTab === 'Clientportaal') loadPortalInvites();
        }
    }, [entity, activeCompanyTab, activeContactTab, entityType, loadClientShortlistBundle]);

    /** Zelfde scope als CRMManager: alleen Direct-klanten; voor portal-klant alleen dochterbedrijven onder eigen tenant. */
    useEffect(() => {
        const needsCompanies =
            isEditingContact ||
            entityType === 'contact' ||
            (entityType === 'company' && activeCompanyTab === 'Contactpersonen');
        if (!needsCompanies || !user) return;

        let cancelled = false;
        (async () => {
            setIsLoadingCompanies(true);
            setCompaniesLoadError(null);
            try {
                let clientQuery = supabase
                    .from('clients')
                    .select('id, name, custom_domain, is_partner')
                    .eq('client_type', 'Direct')
                    .order('name');

                if (user.role === UserRole.CLIENT && user.clientId) {
                    clientQuery = clientQuery.eq('parent_client_id', user.clientId);
                }

                const { data, error } = await clientQuery;
                if (error) throw error;
                if (!cancelled) setAvailableCompanies(data || []);
            } catch (e: any) {
                console.error(e);
                if (!cancelled) {
                    setCompaniesLoadError(e?.message || 'Kon bedrijven niet laden.');
                    setAvailableCompanies([]);
                }
            } finally {
                if (!cancelled) setIsLoadingCompanies(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isEditingContact, entityType, activeCompanyTab, user?.role, user?.clientId]);

    useEffect(() => {
        if (isEditingContact || selectedContact) {
            const cid = crmContactLinkedOrgId(contactForm) || crmContactLinkedOrgId(selectedContact);
            if (cid) loadPortalInvites(cid);
        }
    }, [isEditingContact, selectedContact, contactForm.client_id, contactForm.linked_client_id]);

    const handleSendComm = async () => {
        if (!newCommText.trim() || !user || !entity) return;
        setIsSendingComm(true);
        try {
            await supabase_save_client_communication(entity.id, user.id, newCommText.trim(), commType);
            setNewCommText('');
            loadCommunications();
            setTimeout(() => {
                if (commScrollRef.current) commScrollRef.current.scrollTop = commScrollRef.current.scrollHeight;
            }, 100);
        } catch (e) { console.error(e); } finally { setIsSendingComm(false); }
    };

    const handleInitiateDelete = () => {
        if(isNew || !entity) return;
        setDeleteConfirmInfo({ type: 'main' });
        setDeleteConfirmText('');
        setDeleteError(null);
    };

    const confirmDelete = async () => {
        if (!deleteConfirmInfo) return;
        
        let expectedName: string;
        if (deleteConfirmInfo.type === 'main') {
            expectedName = entityType === 'company' 
                ? (formData.name || '') 
                : (formData.first_name || '').trim();
        } else {
            const targetContact = companyContacts.find(c => c.id === deleteConfirmInfo.id) || contactForm;
            expectedName = (targetContact.first_name || '').trim();
        }

        if (deleteConfirmText.trim().toLowerCase() !== expectedName.trim().toLowerCase()) {
            setDeleteError(`Naam komt niet overeen. Typ exact "${expectedName}".`);
            return;
        }

        setDeleteError(null);
        setIsProcessing(true);
        try {
            if (deleteConfirmInfo.type === 'main') {
                const tableName = entityType === 'company' ? 'clients' : 'client_contacts';
                const { error } = await supabase.from(tableName).delete().eq('id', entity.id);
                if (error) throw error;
                onSaveSuccess();
            } else {
                const { supabase_delete_client_contact } = await import('../services/supabaseService');
                if (!deleteConfirmInfo.id) throw new Error("Geen ID om te verwijderen");
                await supabase_delete_client_contact(deleteConfirmInfo.id);
                setSelectedContact(null);
                setContactForm({});
                setIsEditingContact(false);
                loadCompanyContacts();
            }
        } catch (err: any) {
            console.error(err);
            setDeleteError(
                err.message?.includes('foreign key') || err.message?.includes('violates') 
                ? "Kan niet verwijderen: De contactpersoon of het bedrijf is nog gekoppeld aan actieve vacatures of notities." 
                : "Verwijderen mislukt: " + err.message
            );
        } finally {
            setIsProcessing(false);
            setDeleteConfirmInfo(null);
        }
    };

    const handleInitiateCall = async () => {
        if (!entity?.phone || !recruiterPhone) {
            alert("Zorg dat jij en de klant beiden een telefoonnummer hebben.");
            return;
        }
        setIsCalling(true);
        try {
            const formatE164 = (p: string) => {
                let cleaned = String(p).replace(/\(0\)/g, '').replace(/[^0-9+]/g, '');
                if (cleaned.startsWith('00')) cleaned = '+' + cleaned.substring(2);
                else if (cleaned.startsWith('06')) cleaned = '+31' + cleaned.substring(1);
                else if (cleaned.startsWith('0') && !cleaned.startsWith('00')) cleaned = '+31' + cleaned.substring(1);
                else if (!cleaned.startsWith('+')) cleaned = '+' + cleaned;
                cleaned = cleaned.replace(/^\+310/, '+31'); // Fix commonly mistyped +3106 -> +316
                return cleaned;
            };

            const res = await fetch(`${(import.meta as any).env.VITE_SERVER_URL || ''}/api/bird/call`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    candidatePhone: formatE164(entity.phone),
                    recruiterPhone: formatE164(recruiterPhone),
                    userId: user?.id
                })
            });
            if (res.ok) {
                await supabase_save_client_communication(entity.id, user.id, `Gebeld naar ${entity.phone}`, 'call');
                loadCommunications();
                alert(`Bird SDK: Je wordt nu gebeld op ${recruiterPhone}. Na opnemen wordt de klant gebeld.`);
            } else {
                throw new Error("Call proxy mislukt");
            }
        } catch (err: any) {
            console.error(err);
        } finally {
            setIsCalling(false);
        }
    };

    const handleSaveContact = async () => {
        setIsSavingContact(true);
        try {
            const { supabase_save_client_contact } = await import('../services/supabaseService');
            const linkedOrg =
                String(contactForm.linked_client_id || '').trim() ||
                (String(contactForm.client_id || '').trim() && String(contactForm.client_id) !== WERFTOOLS_DEFAULT_TENANT_CLIENT_ID
                    ? String(contactForm.client_id).trim()
                    : '') ||
                (entityType === 'company' ? entity.id : crmContactLinkedOrgId(entity)) ||
                '';
            const dataToSave = {
                ...contactForm,
                client_id: WERFTOOLS_DEFAULT_TENANT_CLIENT_ID,
                linked_client_id: linkedOrg || null
            };
            if (!dataToSave.id) delete dataToSave.id;

            if (!dataToSave.bullhorn_id) {
                // Fetch max bullhorn_id manually
                const { data: maxRows } = await supabase.from('client_contacts').select('bullhorn_id').not('bullhorn_id', 'is', null);
                let maxVal = 0;
                if (maxRows && maxRows.length > 0) {
                    maxRows.forEach((r: any) => {
                        if (r.bullhorn_id) {
                            const num = parseInt(String(r.bullhorn_id).replace(/\D/g, ''), 10);
                            if (!isNaN(num) && num > maxVal) maxVal = num;
                        }
                    });
                }
                dataToSave.bullhorn_id = (maxVal + 1).toString();
            }
            
            const saved = await supabase_save_client_contact(dataToSave);
            setSelectedContact(saved);
            setIsEditingContact(false);
            loadCompanyContacts();
        } catch (err: any) {
            console.error(err);
            alert("Opslaan mislukt: " + err.message);
        } finally {
            setIsSavingContact(false);
        }
    };

    const handleDeleteContact = (id: string) => {
        setDeleteConfirmInfo({ type: 'contact', id });
        setDeleteConfirmText('');
        setDeleteError(null);
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !entity || !user) return;
        setIsLoadingDocs(true);
        try {
            const uploadedUrl = await supabase_upload_file(file, 'documents', 'client-files/');
            const newDoc = {
                id: crypto.randomUUID(),
                client_id: entity.id,
                title: file.name,
                file_url: uploadedUrl,
                storage_path: `client-files/${file.name}`,
                content: '',
                is_template: false,
                candidate_id: null
            };
            const { error } = await supabase.from('documents').insert([newDoc]);
            if (error) throw error;
            loadCompanyDocuments();
        } catch (error) {
            console.error(error);
            alert("Bestand uploaden mislukt.");
        } finally {
            setIsLoadingDocs(false);
        }
    };

    const handleDeleteDocument = async (id: string, path: string) => {
        if (!confirm('Weet je zeker dat je dit bestand wilt verwijderen?')) return;
        setIsLoadingDocs(true);
        try {
            await supabase_delete_document(id);
            setCompanyDocuments(docs => docs.filter(d => d.id !== id));
        } catch (e) {
            console.error(e);
            alert("Verwijderen mislukt.");
        } finally {
            setIsLoadingDocs(false);
        }
    };

    useEffect(() => {
        if (entity) {
            setFormData({ ...entity });
        } else {
            if (entityType === 'company') {
                setFormData({ name: '', industry: '', address_city: '', phone: '', website: '', client_type: 'Direct' });
            } else {
                setFormData({ first_name: '', last_name: '', email: '', phone: '', job_title: '', client_id: WERFTOOLS_DEFAULT_TENANT_CLIENT_ID, linked_client_id: '', status: 'Active' });
            }
        }
    }, [entity, entityType]);
    
    const handleSubmit = async () => {
        setIsProcessing(true);
        setError(null);

        try {
            const table = entityType === 'company' ? 'clients' : 'client_contacts';
            
            // Clean payload
            const payload = { ...formData };
            if (entityType === 'contact' && payload.clients) delete payload.clients; // Remove relational data before saving
            if (entityType === 'contact') {
                const org = String(payload.linked_client_id || '').trim() || crmContactLinkedOrgId(payload);
                payload.client_id = WERFTOOLS_DEFAULT_TENANT_CLIENT_ID;
                payload.linked_client_id = org || null;
            }
            
            if (entityType === 'company' && user?.clientId && !entity) {
                payload.parent_client_id = user.clientId;
            }

            if (!payload.bullhorn_id) {
                // Fetch max bullhorn_id manually
                const { data: maxRows } = await supabase.from(table).select('bullhorn_id').not('bullhorn_id', 'is', null);
                let maxVal = 0;
                if (maxRows && maxRows.length > 0) {
                    maxRows.forEach((r: any) => {
                        if (r.bullhorn_id) {
                            const num = parseInt(String(r.bullhorn_id).replace(/\D/g, ''), 10);
                            if (!isNaN(num) && num > maxVal) maxVal = num;
                        }
                    });
                }
                payload.bullhorn_id = (maxVal + 1).toString();
            }

            if (entity) {
                const { error: updErr } = await supabase.from(table).update(payload).eq('id', entity.id);
                if (updErr) throw updErr;
            } else {
                const { error: insErr } = await supabase.from(table).insert(payload);
                if (insErr) throw insErr;
            }
            onSaveSuccess();
        } catch (err: any) {
            console.error("Save CRM entity error:", err);
            setError(err.message || 'Kon gegevens niet opslaan.');
        } finally {
            setIsProcessing(false);
        }
    };

    const companyTabs: { id: CompanyTab; icon: React.ReactNode }[] = [
        { id: 'Overzicht', icon: <Building2 size={15} /> },
        { id: 'Communicatie', icon: <Mail size={15} /> },
        { id: 'Contactpersonen', icon: <Users size={15} /> },
        { id: 'Bestanden', icon: <Save size={15} /> },
        { id: 'Shortlist', icon: <LayoutTemplate size={15} /> },
        { id: 'Vacatures', icon: <Briefcase size={15} /> },
        { id: 'Automation', icon: <Loader2 size={15} /> },
    ];

    const contactTabs: { id: ContactTab; icon: React.ReactNode }[] = [
        { id: 'Algemeen', icon: <Users size={15} /> },
        { id: 'Contact Center', icon: <Phone size={15} /> },
        { id: 'Shortlist', icon: <LayoutTemplate size={15} /> },
        { id: 'Vacatures', icon: <Briefcase size={15} /> },
        { id: 'Clientportaal', icon: <ExternalLink size={15} /> },
    ];

    const renderShortlistTab = () => (
        <div className="h-full min-h-0 flex flex-col overflow-hidden bg-[#F8FAFC]">
            <div className="shrink-0 p-4 lg:p-6 border-b border-slate-200 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                        <span className="text-[#e51b23]"><LayoutTemplate size={16} /></span>
                        Shortlist ({clientApplications.length})
                    </h3>
                    <p className="text-[10px] text-slate-500 font-medium mt-1">
                        Zoek op kandidaat, vacature, opdrachtgever of status.
                    </p>
                </div>
                <div className="relative w-full sm:w-72 shrink-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                    <input
                        type="search"
                        placeholder="Zoek kandidaat, vacature, klant…"
                        value={shortlistSearch}
                        onChange={(e) => setShortlistSearch(e.target.value)}
                        className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 outline-none focus:border-[#e51b23] transition-colors"
                    />
                    {shortlistSearch ? (
                        <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            onClick={() => setShortlistSearch('')}
                            aria-label="Zoekopdracht wissen"
                        >
                            <X size={12} />
                        </button>
                    ) : null}
                </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                {isLoadingApps && clientApplications.length === 0 ? (
                    <div className="flex justify-center items-center p-10">
                        <Loader2 className="animate-spin text-slate-400" />
                    </div>
                ) : (
                    <CandidateShortlistPhasesPanel
                        applications={filteredCrmShortlistBundle.applications}
                        interviews={filteredCrmShortlistBundle.interviews}
                        placements={filteredCrmShortlistBundle.placements}
                        stageHistory={filteredCrmShortlistBundle.stageHistory}
                        scope={shortlistScope}
                        onScopeChange={setShortlistScope}
                        onOpenVacancy={(vacancyId) => vacancyId && onNavigateVacancy?.(vacancyId)}
                        onApplicationStatusClick={openCrmShortlistMoveForApplication}
                        focusPhase={crmShortlistFocusPhase}
                        focusPhaseKey={crmShortlistFocusKey}
                    />
                )}
            </div>
        </div>
    );

    const crmTabLabel = (tabId: string) => {
        if (tabId === 'Shortlist') {
            return isLoadingApps ? 'Shortlist' : `Shortlist (${crmShortlistCount})`;
        }
        if (tabId === 'Vacatures') {
            return isLoadingVacancies ? 'Vacatures' : `Vacatures (${latestVacancies.length})`;
        }
        return tabId;
    };

    const crmTabLoading = (tabId: string) => {
        if (tabId === 'Shortlist') return isLoadingApps;
        if (tabId === 'Vacatures') return isLoadingVacancies;
        return false;
    };

    const crmOrgClientIdForShortlist = useMemo(() => {
        if (entityType === 'company' && entity?.id) return String(entity.id);
        if (entityType === 'contact') return crmContactLinkedOrgId(entity);
        return undefined;
    }, [entityType, entity]);

    const openCrmShortlistTab = useCallback(() => {
        const orgId = crmOrgClientIdForShortlist;
        if (orgId) {
            try {
                sessionStorage.setItem(CRM_SHORTLIST_ORG_SESSION_KEY, orgId);
                sessionStorage.setItem(CRM_SHORTLIST_STATUS_FOCUS_KEY, '1');
            } catch {
                /* ignore */
            }
        }
        if (onNavigateToShortlist) {
            onNavigateToShortlist();
            return;
        }
        if (entityType === 'company') setActiveCompanyTab('Shortlist');
        else setActiveContactTab('Shortlist');
    }, [crmOrgClientIdForShortlist, onNavigateToShortlist, entityType]);

    const openCrmShortlistFromJourney = (step: PipelineStepId) => {
        if (entityType === 'company') setActiveCompanyTab('Shortlist');
        else setActiveContactTab('Shortlist');
        setCrmShortlistFocusPhase(pipelineStepToShortlistPhase(step));
        setCrmShortlistFocusKey((k) => k + 1);
    };

    const renderVacaturesTable = () => (
        <div className="h-full min-h-0 flex flex-col overflow-hidden bg-white">
            <div className="shrink-0 p-4 lg:p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                        <span className="text-[#e51b23]"><Briefcase size={16} /></span>
                        Vacatures ({latestVacancies.length})
                    </h3>
                    <p className="text-[10px] text-slate-500 font-medium mt-1">
                        Zoek op titel, nummer, klant, locatie of status.
                    </p>
                </div>
                <div className="relative w-full sm:w-72 shrink-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                    <input
                        type="search"
                        placeholder="Zoek vacature, klant, status…"
                        value={vacanciesSearch}
                        onChange={(e) => setVacanciesSearch(e.target.value)}
                        className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 outline-none focus:border-[#e51b23] transition-colors"
                    />
                    {vacanciesSearch ? (
                        <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            onClick={() => setVacanciesSearch('')}
                            aria-label="Zoekopdracht wissen"
                        >
                            <X size={12} />
                        </button>
                    ) : null}
                </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4 lg:p-6">
                {isLoadingVacancies ? (
                    <div className="flex justify-center items-center p-10">
                        <Loader2 className="animate-spin text-slate-400" />
                    </div>
                ) : (
                    <ClientVacancyListTable
                        vacancies={filteredCrmVacancies}
                        onOpenVacancy={(id) => id && onNavigateVacancy?.(id)}
                        resolveClient={resolveCrmVacancyClient}
                        emptyLabel={
                            latestVacancies.length === 0
                                ? 'Geen vacatures gevonden voor dit bedrijf.'
                                : 'Geen vacatures voor deze zoekopdracht.'
                        }
                    />
                )}
            </div>
        </div>
    );

    const renderCompanyTabContent = () => {
        switch (activeCompanyTab) {
            case 'Overzicht':
                    return (
                    <div className="h-full bg-white overflow-y-auto">
                        <div className="p-4 lg:p-6 space-y-6 animate-fadeIn">
                            
                            {/* Company Details Card */}
                            <div className="bg-white rounded-2xl p-6 lg:p-8 border border-slate-200 shadow-sm relative">
                                {isEditing && (
                                    <div className="absolute top-4 right-4 bg-amber-50 text-amber-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-amber-200 flex items-center gap-1.5">
                                        <Edit size={12}/> Bewerkmodus
                                    </div>
                                )}
                                <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2">
                                    <span className="text-[#e51b23]"><Building2 size={16} /></span> Organisatie Overzicht
                                </h3>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 lg:gap-12">
                                    {/* Left Column: Basis Info & Locatie */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-5">
                                        <h4 className="text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 pb-2 flex items-center gap-2"><Briefcase size={12}/> Basis Info</h4>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center justify-between mb-1.5">
                                                <span>Bedrijfsnaam *</span>
                                                {isEditing && (
                                                    <button onClick={handleSearchKvk} disabled={isSearchingKvk || !formData.name} className="flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-100 hover:bg-orange-100 rounded-lg text-orange-600 hover:text-orange-800 disabled:opacity-50 transition-colors shadow-sm">
                                                        {isSearchingKvk ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} 
                                                        <img src="https://xquintpfpzdsxupnhypj.supabase.co/storage/v1/object/public/public/logos/KVK_logo_RGB30%20(1).png" alt="KVK" className="h-3.5 object-contain opacity-90" />
                                                    </button>
                                                )}
                                            </label>
                                            {isEditing ? (
                                                <div className="relative">
                                                    <input type="text" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" />
                                                    {(kvkResults.length > 0 || kvkSearchedEmpty) && (
                                                        <div className="absolute z-10 w-full mt-2 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden max-h-64 flex flex-col">
                                                            <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500 flex justify-between items-center shrink-0">
                                                                KVK Resultaten
                                                                <button onClick={() => { setKvkResults([]); setKvkSearchedEmpty(null); }} className="hover:text-slate-700 transition-colors"><X size={12}/></button>
                                                            </div>
                                                            <div className="overflow-y-auto custom-scrollbar flex-1">
                                                                {kvkResults.length > 0 ? kvkResults.map((res, i) => (
                                                                    <div key={i} onClick={() => handleSelectKvkResult(res)} className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0 transition-colors">
                                                                        <div className="text-sm font-bold text-slate-900">{res.naam || res.handelsnaam}</div>
                                                                        <div className="text-xs text-slate-500">{res.adres?.binnenlandsAdres?.straatnaam || res.straatnaam} {res.adres?.binnenlandsAdres?.huisnummer || res.huisnummer}{res.adres?.binnenlandsAdres?.huisnummerToevoeging || res.huisnummerToevoeging || ''}, {res.adres?.binnenlandsAdres?.plaats || res.plaats}</div>
                                                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mt-1">KVK: {res.kvkNummer}</div>
                                                                    </div>
                                                                )) : (
                                                                    <div className="p-6 flex flex-col items-center justify-center text-center gap-2">
                                                                        <AlertCircle size={24} className="text-orange-400" />
                                                                        <p className="text-sm font-bold text-slate-700">{kvkSearchedEmpty}</p>
                                                                        <p className="text-xs text-slate-500">Controleer de spelling of pas de bedrijfsnaam aan.</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-sm font-bold text-slate-900 py-1">{formData.name || '-'}</p>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Industrie</label>
                                                {isEditing ? (
                                                    <input type="text" value={formData.industry || ''} onChange={e => setFormData({ ...formData, industry: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" />
                                                ) : (
                                                    <p className="text-sm font-bold text-slate-900 py-1">{formData.industry || '-'}</p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Type (CRM)</label>
                                                {isEditing ? (
                                                    <div className="flex flex-col gap-2 mt-1">
                                                        <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                                                            <input type="checkbox" checked={formData.is_opdrachtgever || false} onChange={e => setFormData({ ...formData, is_opdrachtgever: e.target.checked })} className="rounded text-[#e51b23] focus:ring-[#e51b23] w-4 h-4 cursor-pointer" />
                                                            Opdrachtgever
                                                        </label>
                                                        <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                                                            <input type="checkbox" checked={formData.is_eindklant || false} onChange={e => setFormData({ ...formData, is_eindklant: e.target.checked })} className="rounded text-[#e51b23] focus:ring-[#e51b23] w-4 h-4 cursor-pointer" />
                                                            Eindklant
                                                        </label>
                                                        <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                                                            <input type="checkbox" checked={formData.is_leverancier || false} onChange={e => setFormData({ ...formData, is_leverancier: e.target.checked })} className="rounded text-[#e51b23] focus:ring-[#e51b23] w-4 h-4 cursor-pointer" />
                                                            Leverancier
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-2 flex-wrap py-1 mt-1">
                                                        {formData.is_opdrachtgever && <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border border-emerald-100 shadow-sm">Opdrachtgever</span>}
                                                        {formData.is_eindklant && <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border border-purple-100 shadow-sm">Eindklant</span>}
                                                        {formData.is_leverancier && <span className="bg-slate-50 text-slate-700 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border border-slate-200 shadow-sm">Leverancier</span>}
                                                        {!formData.is_opdrachtgever && !formData.is_eindklant && !formData.is_leverancier && <span className="text-sm font-bold text-slate-500 opacity-80">-</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Bedrijfslogo</label>
                                            {isEditing ? (
                                                <div className="flex items-center gap-2">
                                                    <label className="cursor-pointer flex flex-col items-center justify-center p-3 border border-slate-200 rounded-xl bg-slate-50 hover:border-[#e51b23] hover:text-[#e51b23] transition-colors h-16 w-32 relative overflow-hidden group">
                                                        {formData.logo_url ? (
                                                            <>
                                                                <img src={formData.logo_url} alt="Logo" className="h-full object-contain" />
                                                                <div className="absolute inset-0 bg-white/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                                    <Upload size={14} className="text-[#e51b23]" />
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 group-hover:text-[#e51b23] flex items-center gap-1.5">
                                                                {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12}/>} Upload
                                                            </div>
                                                        )}
                                                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={isProcessing || !entity} />
                                                        {!entity && <span className="absolute text-[8px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded -top-2">Eerst opslaan</span>}
                                                    </label>
                                                    <button onClick={() => setShowLogoSearch(true)} disabled={!formData.name || !entity || isProcessing} className="h-16 px-4 bg-indigo-50 border border-indigo-200 rounded-xl text-indigo-600 hover:bg-indigo-100 flex flex-col items-center justify-center gap-1.5 transition-colors disabled:opacity-50">
                                                        <Bot size={16} />
                                                        <span className="text-[9px] font-black uppercase tracking-widest whitespace-nowrap">Zoek logo</span>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="py-1">
                                                    {formData.logo_url ? <img src={formData.logo_url} alt="Logo" className="h-10 object-contain rounded" /> : <p className="text-sm font-bold text-slate-400 opacity-80">-</p>}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Website</label>
                                            {isEditing ? (
                                                <input type="url" value={formData.website || ''} onChange={e => setFormData({ ...formData, website: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" placeholder="https://" />
                                            ) : (
                                                <a href={formData.website} target="_blank" rel="noreferrer" className="text-sm font-bold text-blue-600 hover:text-blue-700 hover:underline py-1 block truncate flex items-center gap-1.5">
                                                    {formData.website || '-'}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-5">
                                        <h4 className="text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 pb-2 flex items-center gap-2"><MapPin size={12}/> Locatie & Contact</h4>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Straat & Huisnummer</label>
                                            {isEditing ? (
                                                <input type="text" value={formData.address_street || ''} onChange={e => setFormData({ ...formData, address_street: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" />
                                            ) : (
                                                <p className="text-sm font-bold text-slate-900 py-1">{formData.address_street || '-'}</p>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Postcode</label>
                                                {isEditing ? (
                                                    <input type="text" value={formData.address_zip || ''} onChange={e => setFormData({ ...formData, address_zip: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" />
                                                ) : (
                                                    <p className="text-sm font-bold text-slate-900 py-1">{formData.address_zip || '-'}</p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Plaats</label>
                                                {isEditing ? (
                                                    <input type="text" value={formData.address_city || ''} onChange={e => setFormData({ ...formData, address_city: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" />
                                                ) : (
                                                    <p className="text-sm font-bold text-slate-900 py-1">{formData.address_city || '-'}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Telefoon</label>
                                                {isEditing ? (
                                                    <input type="text" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" />
                                                ) : (
                                                    <p className="text-sm font-bold text-slate-900 py-1 flex items-center gap-2">
                                                        {formData.phone || '-'}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">E-mail</label>
                                                {isEditing ? (
                                                    <input type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all" />
                                                ) : (
                                                    <a href={formData.email ? `mailto:${formData.email}` : undefined} className="text-sm font-bold text-blue-600 hover:text-blue-700 hover:underline py-1 block truncate">
                                                        {formData.email || '-'}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    
                                    {/* KVK Informatie Blok */}
                                    <div className="space-y-5 pt-8 border-t border-slate-100">
                                        <h4 className="text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 pb-2 flex items-center gap-2"><Landmark size={12}/> KvK Profiel & Erkenning</h4>
                                        <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-5 shadow-sm relative overflow-hidden">
                                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                                <Landmark size={64} className="text-blue-500" />
                                            </div>
                                            <div className="relative z-10 grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">KvK Nummer</label>
                                                    <p className="text-sm font-bold text-slate-900">{formData.invoice_kvk || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Vestigingsnr. / RSIN</label>
                                                    <p className="text-sm font-bold text-slate-900">{formData.data?.rsin || '-'}</p>
                                                </div>
                                                <div className="col-span-2 mt-2">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Branche & Activiteit (SBI)</label>
                                                    <p className="text-xs font-bold text-slate-700 bg-white px-3 py-2 rounded-lg border border-slate-200 mt-1">{formData.data?.sbi || '-'}</p>
                                                </div>
                                                <div className="col-span-2 mt-2">
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Rechtsvorm</label>
                                                    <p className="text-sm font-bold text-slate-900">{formData.data?.rechtsvorm || '-'}</p>
                                                </div>
                                            </div>
                                            {(formData.data?.rsin || formData.data?.sbi) && (
                                                <div className="mt-4 pt-4 border-t border-blue-100 flex items-center gap-2 text-[10px] font-black text-blue-600 uppercase tracking-widest">
                                                    <CheckCircle2 size={12} /> Gevalideerd KVK Profiel
                                                </div>
                                            )}
                                        </div>
                                    </div>

{/* Clientportaal Toegang */}
                                    <div className="space-y-5 pt-8 border-t border-slate-100">
                                        <h4 className="text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 pb-2 flex items-center gap-2"><Globe size={12}/> Clientportaal Toegang</h4>
                                        <div className="grid grid-cols-1 gap-4">
                                            {/* Clientportaal */}
                                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                                                <div className="flex items-start justify-between mb-3">
                                                    <div>
                                                        <p className="text-xs font-black text-slate-900 uppercase tracking-widest">Toegang Clientportaal</p>
                                                        <p className="text-[10px] text-slate-500 font-medium">Alle contactpersonen van dit bedrijf kunnen inloggen op uw portaal.</p>
                                                    </div>
                                                    {isEditing ? (
                                                        <label className="relative inline-flex items-center cursor-pointer mt-1">
                                                            <input type="checkbox" checked={formData.active_clientportal || false} onChange={e => setFormData({ ...formData, active_clientportal: e.target.checked })} className="sr-only peer" />
                                                            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500 shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"></div>
                                                        </label>
                                                    ) : (
                                                        formData.active_clientportal ? (
                                                            <span className="bg-emerald-50 text-emerald-700 font-black uppercase tracking-widest text-[9px] px-2 py-0.5 rounded border border-emerald-200 shadow-sm flex items-center gap-1"><CheckCircle2 size={10}/> Actief</span>
                                                        ) : (
                                                            <span className="bg-slate-100 text-slate-400 font-black uppercase tracking-widest text-[9px] px-2 py-0.5 rounded border border-slate-200 shadow-sm">Inactief</span>
                                                        )
                                                    )}
                                                </div>
                                                
                                                <div className="mt-4 pt-4 border-t border-slate-200">
                                                    <p className="text-[10px] font-black uppercase text-slate-400 mb-2">Portaal Link voor Klant</p>
                                                    {partnerDomain ? (
                                                        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3">
                                                            <a href={`https://${partnerDomain}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2 truncate">
                                                                <Globe size={14} className="shrink-0"/> {partnerDomain}
                                                            </a>
                                                        </div>
                                                    ) : (
                                                        <p className="text-[11px] font-medium text-slate-500 italic">Uw Systeembeheerder heeft nog geen eigen portaaldomein ingesteld.</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column: Bedrijfsprofiel */}
                                <div className="flex flex-col h-full space-y-5 xl:border-l xl:border-t-0 border-t border-slate-100 pt-8 xl:pt-0 xl:pl-10">
                                    <h4 className="text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 pb-2 flex items-center gap-2"><AlignLeft size={12}/> Bedrijfsprofiel</h4>
                                    <div className="h-full flex flex-col pt-1 min-h-[250px]">
                                        {isEditing ? (
                                            <AiRichTextEditor 
                                                value={formData.organizationDescription || ''}
                                                onChange={val => setFormData({ ...formData, organizationDescription: val })}
                                                placeholder={`Schrijf of genereer automatisch een wervende introductie over ${formData.name || 'dit bedrijf'}...`}
                                                minHeight="200px"
                                                className="flex-1 max-h-[600px] overflow-y-auto"
                                            />
                                        ) : (
                                            <div 
                                                className="prose prose-sm max-w-none text-slate-700 bg-slate-50 p-4 rounded-xl border border-slate-100 flex-1 overflow-y-auto custom-scrollbar"
                                                dangerouslySetInnerHTML={{ __html: formData.organizationDescription || '<span class="text-slate-400 italic text-sm">Geen bedrijfsprofiel ingevuld...</span>' }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                            {/* Two-Column Bottom Layout: Vacancies & Shortlist Overview */}
                            {!isNew && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Recent Vacancies */}
                                    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col">
                                        <div className="flex justify-between items-center mb-5">
                                            <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                                <span className="text-purple-600"><Briefcase size={14}/></span> Recente Vacatures
                                            </h3>
                                        </div>
                                        
                                        {isLoadingVacancies ? (
                                            <div className="flex-1 flex justify-center items-center py-10"><Loader2 className="animate-spin text-slate-300" /></div>
                                        ) : latestVacancies.length === 0 ? (
                                            <div className="bg-slate-50 rounded-xl border border-dashed border-slate-200 p-8 text-center flex-1 flex flex-col justify-center items-center">
                                                <Briefcase size={24} className="text-slate-300 mb-2"/>
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Geen vacatures gevonden</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3 flex-1">
                                                {latestVacancies.map(vac => (
                                                    <div key={vac.id} className="group p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-purple-200 hover:shadow-sm transition-all flex justify-between items-center">
                                                        <div>
                                                            <div className="font-bold text-sm text-slate-900 group-hover:text-purple-700 transition-colors">{vac.title}</div>
                                                            <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mt-1 flex items-center gap-2">
                                                                {vac.location} • <span className={`px-2 rounded-sm text-[9px] ${vac.status === 'Open' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{vac.status}</span>
                                                            </div>
                                                        </div>
                                                        <ChevronRight size={16} className="text-slate-300 group-hover:text-purple-500 transition-colors"/>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Quick Shortlist (First 5 Active Candidates) */}
                                    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col">
                                        <div className="flex justify-between items-center mb-5">
                                            <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                                <span className="text-blue-600"><Users size={14}/></span> Shortlist
                                            </h3>
                                            <button onClick={openCrmShortlistTab} className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:text-blue-800 transition-colors">Volledige lijst &rarr;</button>
                                        </div>

                                        {isLoadingApps ? (
                                            <div className="flex-1 flex justify-center items-center py-10"><Loader2 className="animate-spin text-slate-300" /></div>
                                        ) : clientApplications.length === 0 ? (
                                            <div className="bg-slate-50 rounded-xl border border-dashed border-slate-200 p-8 text-center flex-1 flex flex-col justify-center items-center">
                                                <Users size={24} className="text-slate-300 mb-2"/>
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Geen kandidaten in procedure</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3 flex-1">
                                                {clientApplications.slice(0, 5).map(app => (
                                                    <div key={app.id} onClick={() => app.candidate_id && onNavigateCandidate?.(app.candidate_id)} className={`group p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-blue-200 hover:shadow-sm transition-all flex justify-between items-center ${app.candidate_id && onNavigateCandidate ? 'cursor-pointer' : ''}`}>
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-black text-xs flex items-center justify-center shrink-0 overflow-hidden">
                                                                {app.candidates?.photo_url ? <img src={app.candidates.photo_url} className="w-full h-full object-cover" /> : (app.candidates?.name?.charAt(0) || 'K')}
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-sm text-slate-900 group-hover:text-blue-700 transition-colors">{app.candidates?.name || 'Onbekend'}</div>
                                                                <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mt-0.5 flex items-center gap-1.5 line-clamp-1 truncate w-40">
                                                                    <span>{app.vacancies?.title || '-'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="text-right flex flex-col items-end">
                                                            <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest whitespace-nowrap border border-slate-300 shadow-sm">{app.status || 'Nieuw'}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                );
            case 'Communicatie':
                return (
                    <div className="h-full flex flex-col min-h-0 bg-[#f8fafc]">
                        {/* Header bar */}
                        <div className="px-6 py-4 bg-white border-b border-slate-100 shrink-0 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center text-[#e51b23]">
                                    <Mail size={16} />
                                </div>
                                <div>
                                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Communicatie & Notities</h3>
                                    <p className="text-[10px] text-slate-400 font-medium">{communications.length} berichten</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={handleInitiateCall} disabled={isCalling} className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-200 disabled:opacity-50 active:scale-95">
                                    {isCalling ? <Loader2 size={12} className="animate-spin" /> : <Phone size={12} />} Bellen
                                </button>
                                <button onClick={() => setCommType('whatsapp')} className="flex items-center gap-1.5 bg-[#f0fdf4] text-[#16a34a] hover:bg-[#dcfce7] transition-colors px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-[#bbf7d0] active:scale-95">
                                    <MessageSquare size={12} /> WhatsApp
                                </button>
                            </div>
                        </div>

                        {/* Messages feed */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-3" ref={commScrollRef}>
                            {communications.length === 0 && (
                                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4 text-slate-300">
                                        <Mail size={28} />
                                    </div>
                                    <p className="text-sm font-bold text-slate-400">Nog geen communicatie</p>
                                    <p className="text-xs text-slate-400 mt-1 font-medium">Stuur een notitie of bericht hieronder</p>
                                </div>
                            )}
                            {communications.map((comm) => {
                                const displayContent = comm.content.split('---RAW_TRANSCRIPT---')[0];
                                const isOwn = comm.user_id === user?.id;
                                const typeColors: Record<string, string> = {
                                    note: 'bg-amber-50 border-amber-100 text-amber-700',
                                    email: 'bg-blue-50 border-blue-100 text-blue-700',
                                    phone: 'bg-emerald-50 border-emerald-100 text-emerald-700',
                                    whatsapp: 'bg-green-50 border-green-100 text-green-700',
                                    call: 'bg-purple-50 border-purple-100 text-purple-700',
                                };
                                const typeColor = typeColors[comm.type] || 'bg-slate-100 border-slate-200 text-slate-600';
                                return (
                                    <div key={comm.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}>
                                        <div className={`max-w-[78%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                                            {/* Meta */}
                                            <div className={`flex items-center gap-2 px-1 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                                                <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${typeColor}`}>{comm.type}</span>
                                                <span className="text-[10px] text-slate-400 font-medium">{comm.profiles?.name || 'Jij'} · {new Date(comm.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                            {/* Bubble */}
                                            <div className={`px-4 py-3 rounded-2xl text-sm font-medium leading-relaxed whitespace-pre-wrap shadow-sm ${isOwn ? 'bg-slate-900 text-white rounded-tr-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm'}`}>
                                                {displayContent}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Composer */}
                        <div className="shrink-0 bg-white border-t border-slate-100 p-4">
                            {/* Type pill selector */}
                            <div className="flex gap-1.5 mb-3">
                                {(['note', 'email', 'call', 'whatsapp'] as const).map(t => {
                                    const icons: Record<string, React.ReactNode> = { note: <FileText size={11} />, email: <Mail size={11} />, call: <Phone size={11} />, whatsapp: <MessageSquare size={11} /> };
                                    return (
                                        <button
                                            key={t}
                                            onClick={() => setCommType(t)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${commType === t ? 'bg-slate-900 text-white border-slate-900 shadow-sm' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300 hover:text-slate-600'}`}
                                        >
                                            {icons[t]} {t}
                                        </button>
                                    );
                                })}
                            </div>
                            {/* Text input */}
                            <div className="relative">
                                <textarea
                                    value={newCommText}
                                    onChange={(e) => setNewCommText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSendComm(); }}
                                    placeholder={`Schrijf een ${commType}... (Ctrl+Enter om te versturen)`}
                                    className="w-full pl-4 pr-14 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] resize-none h-[80px] custom-scrollbar transition-colors"
                                />
                                <button
                                    onClick={handleSendComm}
                                    disabled={isSendingComm || !newCommText.trim()}
                                    className="absolute bottom-3 right-3 w-9 h-9 bg-[#e51b23] hover:bg-red-700 text-white rounded-lg flex items-center justify-center disabled:opacity-40 transition-all shadow-md active:scale-95"
                                    title="Versturen (Ctrl+Enter)"
                                >
                                    {isSendingComm ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'Contactpersonen':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-8 overflow-y-auto custom-scrollbar">
                        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm w-full space-y-8 animate-fadeIn">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                    <span className="text-[#e51b23]"><Users size={16} /></span> Gekoppelde Contactpersonen
                                </h3>
                                <button 
                                    onClick={() => {
                                        setContactForm({
                                            status: 'Active',
                                            client_id: WERFTOOLS_DEFAULT_TENANT_CLIENT_ID,
                                            linked_client_id: entityType === 'company' ? entity.id : crmContactLinkedOrgId(entity)
                                        });
                                        setIsEditingContact(true);
                                        setSelectedContact({});
                                    }}
                                    className="px-4 py-2 bg-[#e51b23] text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-black transition-colors flex items-center gap-2 shadow-sm"
                                >
                                    <Plus size={14} /> Nieuw Contact
                                </button>
                            </div>
                            
                            {isLoadingContacts ? (
                                <div className="flex justify-center p-10"><Loader2 className="animate-spin text-slate-400" /></div>
                            ) : companyContacts.length === 0 ? (
                                <div className="text-center p-10 text-slate-400 text-xs font-bold uppercase tracking-widest">
                                    Geen contactpersonen gevonden.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm border-collapse">
                                        <thead>
                                            <tr className="border-b border-slate-100 bg-slate-50">
                                                <th className="p-3 font-black text-[10px] text-slate-400 uppercase tracking-widest">Aanhef & Naam</th>
                                                <th className="p-3 font-black text-[10px] text-slate-400 uppercase tracking-widest">Afdeling & Functie</th>
                                                <th className="p-3 font-black text-[10px] text-slate-400 uppercase tracking-widest">Contact</th>
                                                <th className="p-3 font-black text-[10px] text-slate-400 uppercase tracking-widest">Acties</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {companyContacts.map(contact => (
                                                <tr key={contact.id} onClick={() => { setSelectedContact(contact); setIsEditingContact(false); setContactForm(contact); }} className="hover:bg-slate-50/80 transition-colors cursor-pointer group">
                                                    <td className="p-3">
                                                        <div className="font-bold text-slate-900 flex items-center gap-2">
                                                            {contact.salutation && <span className="text-slate-400 text-xs">{contact.salutation}</span>}
                                                            {contact.first_name} {contact.last_name}
                                                            <div className={`w-1.5 h-1.5 rounded-full ${contact.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-300'}`} title={contact.status}></div>
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        <div className="flex flex-col gap-0.5">
                                                            <span className="text-slate-600 font-bold">{contact.job_title || '-'}</span>
                                                            {contact.department && <span className="text-[10px] font-bold uppercase tracking-widest text-[#e51b23]">{contact.department}</span>}
                                                        </div>
                                                    </td>
                                                    <td className="p-3 text-slate-600">
                                                        <div className="flex flex-col gap-1 text-[11px]">
                                                            {(contact.email || contact.personal_email) && <span className="flex items-center gap-1.5"><Mail size={10} className="text-slate-400"/> {contact.email || contact.personal_email}</span>}
                                                            {(contact.phone || contact.work_phone || contact.mobile_phone) && <span className="flex items-center gap-1.5"><Phone size={10} className="text-slate-400"/> {contact.phone || contact.work_phone || contact.mobile_phone}</span>}
                                                        </div>
                                                    </td>
                                                    <td className="p-3 text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <button className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-500 group-hover:bg-slate-900 group-hover:text-white rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm">
                                                              Details <ChevronRight size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 'Bestanden':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-8 overflow-y-auto custom-scrollbar">
                        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm max-w-5xl mx-auto space-y-8 animate-fadeIn">
                             <div className="flex justify-between items-center mb-6">
                                <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                    <span className="text-[#e51b23]"><Save size={16} /></span> Bestanden & Documenten
                                </h3>
                                <label className="cursor-pointer px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors rounded-xl text-xs font-black uppercase tracking-widest border border-emerald-200 flex items-center gap-2">
                                    {isLoadingDocs ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} 
                                    Uploaden
                                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={isLoadingDocs} />
                                </label>
                            </div>

                            {isLoadingDocs && companyDocuments.length === 0 ? (
                                <div className="flex justify-center p-10"><Loader2 className="animate-spin text-slate-400" /></div>
                            ) : companyDocuments.length === 0 ? (
                                <div className="text-center p-10 text-slate-400 text-xs font-bold uppercase tracking-widest border-2 border-dashed border-slate-100 rounded-xl bg-slate-50">
                                    Nog geen bestanden geüpload voor deze klant.
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {companyDocuments.map(doc => (
                                        <div key={doc.id} className="flex items-center gap-4 p-4 border border-slate-100 rounded-xl bg-slate-50 hover:border-slate-300 transition-all group">
                                            <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center shrink-0 text-[#e51b23]">
                                                {doc.file_url?.toLowerCase().endsWith('.pdf') ? <FileText size={18} /> : <File size={18} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-bold text-slate-900 truncate" title={doc.title}>{doc.title}</div>
                                                <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{new Date(doc.created_at).toLocaleDateString()}</div>
                                            </div>
                                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-emerald-600 bg-white rounded-lg border border-slate-200 shadow-sm" title="Bekijken">
                                                    <FileText size={14} />
                                                </a>
                                                <button onClick={() => handleDeleteDocument(doc.id, doc.storage_path)} className="p-2 text-slate-400 hover:text-red-600 bg-white rounded-lg border border-slate-200 shadow-sm" title="Verwijderen">
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 'Shortlist':
                return renderShortlistTab();
            case 'Vacatures':
                return renderVacaturesTable();
            case 'Automation':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-8 overflow-y-auto custom-scrollbar">
                        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm max-w-5xl mx-auto space-y-8 animate-fadeIn">
                             <div className="flex justify-between items-center mb-6">
                                <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                    <span className="text-[#e51b23]"><Wrench size={16} /></span> Actieve Automations
                                </h3>
                                <button className="px-4 py-2 bg-slate-900 text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-[#e51b23] transition-colors flex items-center gap-2">
                                    <Plus size={14} /> Nieuwe Flow
                                </button>
                            </div>
                            
                            {isLoadingFlows ? (
                                <div className="flex justify-center p-10"><Loader2 className="animate-spin text-slate-400" /></div>
                            ) : automationFlows.length === 0 ? (
                                <div className="text-center p-10 text-slate-400 text-[11px] font-bold uppercase tracking-widest border border-dashed border-slate-200 bg-slate-50 rounded-xl">
                                    Geen actieve flows voor deze klant.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {automationFlows.map(flow => (
                                        <div key={flow.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50 flex items-center justify-between">
                                            <div>
                                                <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                                                    {flow.name}
                                                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${flow.status === 'active' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                                                        {flow.status}
                                                    </span>
                                                </div>
                                                <div className="text-[10px] text-slate-500 font-medium uppercase tracking-wider mt-1 flex items-center gap-2">
                                                    Trigger: {flow.trigger} {flow.trigger_config?.vacancy_id ? `(Vacature)` : ''}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                {flow.steps.map((step: any, idx: number) => (
                                                    <div key={step.id} className="flex items-center">
                                                        {idx > 0 && <ChevronRight size={12} className="text-slate-300 mx-1" />}
                                                        <span className="bg-white px-2 py-1 rounded border border-slate-200 text-[10px] font-bold text-slate-600 shadow-sm flex items-center gap-1">
                                                            {step.type === 'email' && <Mail size={10} />}
                                                            {step.type === 'whatsapp' && <MessageSquare size={10} />}
                                                            {step.type === 'stage_change' && <LayoutTemplate size={10} />}
                                                            {step.type === 'delay' && <Calendar size={10} />}
                                                            {step.name || step.type}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
        }
    };

    const renderContactTabContent = () => {
        switch (activeContactTab) {
            case 'Algemeen':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-8 overflow-y-auto custom-scrollbar">
                        <div className="bg-white rounded-2xl p-6 lg:p-8 border border-slate-200 shadow-sm max-w-4xl space-y-10 animate-fadeIn mx-auto relative">
                            {isNew && entityType === 'contact' && (
                                <div className="rounded-xl border border-sky-200 bg-sky-50/90 p-4 text-sm text-slate-800">
                                    <p className="text-[11px] font-black uppercase tracking-widest text-sky-900 mb-1.5">Bedrijf eerst</p>
                                    <p className="text-xs text-slate-700 leading-relaxed">
                                        Een contactpersoon koppel je altijd aan een <strong>bedrijf</strong>. Kies het bedrijf hieronder, of ga naar tab{' '}
                                        <strong>Bedrijven</strong>, open het juiste bedrijf en voeg iemand toe via <strong>Contactpersonen</strong> op het bedrijfsprofiel — dan staat het bedrijf al vast.
                                    </p>
                                    {onNavigateToCompanies ? (
                                        <button
                                            type="button"
                                            onClick={onNavigateToCompanies}
                                            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-sky-300 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-sky-900 shadow-sm transition hover:bg-sky-100"
                                        >
                                            <Building2 size={14} /> Naar tab Bedrijven
                                        </button>
                                    ) : null}
                                </div>
                            )}
                            {isEditing && (
                                <div className="absolute top-6 right-6 flex items-center gap-3">
                                    <button 
                                        onClick={() => {
                                            if (confirm("Weet je zeker dat je alle contactvelden leeg wilt maken?")) {
                                                setFormData({
                                                    ...formData,
                                                    salutation: '',
                                                    first_name: '',
                                                    last_name: '',
                                                    client_id: '',
                                                    linked_client_id: '',
                                                    department: '',
                                                    job_title: '',
                                                    status: 'Active',
                                                    email: '',
                                                    personal_email: '',
                                                    work_phone: '',
                                                    phone: '',
                                                    home_phone: '',
                                                    address: '',
                                                    experience: '',
                                                    categories: '',
                                                    branches: '',
                                                    remarks: ''
                                                });
                                            }
                                        }}
                                        className="bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-red-200 flex items-center gap-1.5 transition-colors"
                                    >
                                        <Trash2 size={12}/> ALLES WISSEN
                                    </button>
                                    <div className="bg-amber-50 text-amber-600 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-amber-200 flex items-center gap-1.5">
                                        <Edit size={12}/> Bewerkmodus
                                    </div>
                                </div>
                            )}

                            {/* Section 1: Persoonsgegevens & Werk */}
                            <div>
                                <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2 border-b border-slate-100 pb-3">
                                    <span className="text-[#e51b23]"><Users size={16} /></span> Persoonsgegevens & Werk
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Aanhef</label>
                                        {isEditing ? (
                                            <input type="text" placeholder="bijv. Dhr. of Mevr." value={formData.salutation || ''} onChange={e => setFormData({ ...formData, salutation: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{formData.salutation || '-'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Voornaam *</label>
                                        {isEditing ? (
                                            <input type="text" required value={formData.first_name || ''} onChange={e => setFormData({ ...formData, first_name: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{formData.first_name || '-'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Achternaam</label>
                                        {isEditing ? (
                                            <input type="text" value={formData.last_name || ''} onChange={e => setFormData({ ...formData, last_name: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{formData.last_name || '-'}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Status</label>
                                        {isEditing ? (
                                            <select value={formData.status || 'Active'} onChange={e => setFormData({ ...formData, status: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]">
                                                <option value="Active">Actief</option>
                                                <option value="Archive">Nonactief (Archief)</option>
                                                <option value="New Lead">Nieuwe Lead</option>
                                            </select>
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{formData.status === 'Active' ? 'Actief' : formData.status === 'Archive' ? 'Nonactief' : 'Nieuwe Lead'}</p>
                                        )}
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Bedrijf *</label>
                                        {isEditing ? (
                                            <>
                                                <SearchableCombobox
                                                    options={buildCRMClientComboboxOptions(availableCompanies, entityType, entity)}
                                                    value={crmContactLinkedOrgId(formData) || ''}
                                                    onChange={(val) =>
                                                        setFormData({
                                                            ...formData,
                                                            client_id: WERFTOOLS_DEFAULT_TENANT_CLIENT_ID,
                                                            linked_client_id: val,
                                                        })
                                                    }
                                                    placeholder="Selecteer een bedrijf..."
                                                    searchPlaceholder="Zoek bedrijfsnaam..."
                                                    icon={Building2}
                                                    className="w-full"
                                                    isLoading={isLoadingCompanies}
                                                    emptyText={
                                                        !isLoadingCompanies && availableCompanies.length === 0
                                                            ? 'Geen bedrijven gevonden. Maak eerst een bedrijf aan (tab Bedrijven) of controleer je rechten.'
                                                            : 'Geen resultaten.'
                                                    }
                                                />
                                                {companiesLoadError ? (
                                                    <p className="mt-1.5 text-xs font-semibold text-red-600">{companiesLoadError}</p>
                                                ) : null}
                                                {!isLoadingCompanies && !companiesLoadError && availableCompanies.length === 0 ? (
                                                    <p className="mt-1.5 text-xs font-medium text-amber-800">
                                                        Er zijn nog geen bedrijven om te koppelen. Gebruik tab <strong>Bedrijven</strong> om een bedrijf aan te maken, of voeg een contactpersoon toe vanuit het bedrijfsprofiel.
                                                    </p>
                                                ) : null}
                                            </>
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{availableCompanies.find(c => c.id === crmContactLinkedOrgId(formData))?.name || '-'}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Afdeling</label>
                                        {isEditing ? (
                                            <input type="text" value={formData.department || ''} onChange={e => setFormData({ ...formData, department: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{formData.department || '-'}</p>
                                        )}
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Huidige Functietitel</label>
                                        {isEditing ? (
                                            <input type="text" value={formData.job_title || ''} onChange={e => setFormData({ ...formData, job_title: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{formData.job_title || '-'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Contactgegevens */}
                            <div>
                                <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2 border-b border-slate-100 pb-3">
                                    <span className="text-[#e51b23]"><Phone size={16} /></span> Contactgegevens
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">E-mail op werk</label>
                                        {isEditing ? (
                                            <input type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <a href={formData.email ? `mailto:${formData.email}` : undefined} className="text-sm font-bold text-blue-600 hover:underline py-1 block truncate">{formData.email || '-'}</a>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Persoonlijke e-mail</label>
                                        {isEditing ? (
                                            <input type="email" value={formData.personal_email || ''} onChange={e => setFormData({ ...formData, personal_email: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <a href={formData.personal_email ? `mailto:${formData.personal_email}` : undefined} className="text-sm font-bold text-blue-600 hover:underline py-1 block truncate">{formData.personal_email || '-'}</a>
                                        )}
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Telefoon werk</label>
                                        {isEditing ? (
                                            <input type="text" value={formData.work_phone || ''} onChange={e => setFormData({ ...formData, work_phone: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{formData.work_phone || '-'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Mobiele telefoon</label>
                                        {isEditing ? (
                                            <input type="text" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{formData.phone || '-'}</p>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Telefoon thuis</label>
                                        {isEditing ? (
                                            <input type="text" value={formData.home_phone || ''} onChange={e => setFormData({ ...formData, home_phone: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-bold text-slate-900 py-1">{formData.home_phone || '-'}</p>
                                        )}
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Adres</label>
                                        {isEditing ? (
                                            <textarea rows={2} value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] custom-scrollbar" />
                                        ) : (
                                            <p className="text-sm font-medium text-slate-900 py-1 whitespace-pre-wrap">{formData.address || '-'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Extra Informatie */}
                            <div>
                                <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2 border-b border-slate-100 pb-3">
                                    <span className="text-[#e51b23]"><AlignLeft size={16} /></span> Extra Informatie
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div className="md:col-span-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Ervaring</label>
                                        {isEditing ? (
                                            <textarea rows={3} value={formData.experience || ''} onChange={e => setFormData({ ...formData, experience: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] custom-scrollbar" />
                                        ) : (
                                            <p className="text-sm font-medium text-slate-900 py-1 whitespace-pre-wrap">{formData.experience || '-'}</p>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Categorieën</label>
                                        {isEditing ? (
                                            <input type="text" placeholder="bijv. Software, Management" value={formData.categories || ''} onChange={e => setFormData({ ...formData, categories: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-medium text-slate-900 py-1">{formData.categories || '-'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Branches</label>
                                        {isEditing ? (
                                            <input type="text" placeholder="bijv. IT, Bouw, Zorg" value={formData.branches || ''} onChange={e => setFormData({ ...formData, branches: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23]" />
                                        ) : (
                                            <p className="text-sm font-medium text-slate-900 py-1">{formData.branches || '-'}</p>
                                        )}
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Algemene Opmerkingen</label>
                                        {isEditing ? (
                                            <textarea rows={4} value={formData.remarks || ''} onChange={e => setFormData({ ...formData, remarks: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] custom-scrollbar" />
                                        ) : (
                                            <p className="text-sm font-medium text-slate-900 py-1 whitespace-pre-wrap">{formData.remarks || '-'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: Clientportaal Toegang */}
                            <div className="pt-8 border-t border-slate-100">
                                <h3 className="text-[12px] font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2 border-b border-slate-100 pb-3">
                                    <span className="text-[#e51b23]"><Globe size={16} /></span> Clientportaal Toegang
                                </h3>
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-xs font-black text-slate-900 uppercase tracking-widest">Toegang Clientportaal</p>
                                            <p className="text-[10px] text-slate-500 font-medium">Bepaalt of deze contactpersoon mag inloggen (bij een actief bedrijfsportaal).</p>
                                        </div>
                                        {isEditing ? (
                                            <label className="relative inline-flex items-center cursor-pointer mt-1">
                                                <input type="checkbox" checked={formData.active_clientportal !== false} onChange={e => setFormData({ ...formData, active_clientportal: e.target.checked })} className="sr-only peer" />
                                                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500 shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"></div>
                                            </label>
                                        ) : (
                                            formData.active_clientportal !== false ? (
                                                <span className="bg-emerald-50 text-emerald-700 font-black uppercase tracking-widest text-[9px] px-2 py-0.5 rounded border border-emerald-200 shadow-sm flex items-center gap-1"><CheckCircle2 size={10}/> Toegestaan</span>
                                            ) : (
                                                <span className="bg-red-50 text-red-600 font-black uppercase tracking-widest text-[9px] px-2 py-0.5 rounded border border-red-200 shadow-sm">Geblokkeerd</span>
                                            )
                                        )}
                                    </div>
                                    
                                    <div className="mt-4 pt-4 border-t border-slate-200">
                                        <p className="text-[10px] font-black uppercase text-slate-400 mb-2">Portaal Link voor Klant</p>
                                        {partnerDomain ? (
                                            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3">
                                                <a href={`https://${partnerDomain}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2 truncate">
                                                    <Globe size={14} className="shrink-0"/> {partnerDomain}
                                                </a>
                                            </div>
                                        ) : (
                                            <p className="text-[11px] font-medium text-slate-500 italic">Uw Systeembeheerder heeft nog geen eigen portaaldomein ingesteld.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'Contact Center':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-8 overflow-y-auto custom-scrollbar flex items-center justify-center">
                        <div className="text-center p-8 bg-white border border-slate-200 rounded-2xl shadow-sm max-w-lg">
                            <Phone size={32} className="mx-auto text-slate-300 mb-4" />
                            <h3 className="text-sm font-black text-slate-900 mb-2 uppercase tracking-widest">Geen specifieke Contact Center velden meer.</h3>
                            <p className="text-xs text-slate-500 font-medium">Alle communicatie velden (Telefoon, Email) zijn gemigreerd naar de 'Algemeen' tab voor een overzichtelijkere ervaring.</p>
                            <button onClick={() => setActiveContactTab('Algemeen')} className="mt-6 px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest">
                                Naar Algemeen
                            </button>
                        </div>
                    </div>
                );
            case 'Shortlist':
                return renderShortlistTab();
            case 'Vacatures':
                return renderVacaturesTable();
            case 'Clientportaal':
                return renderClientportaalTab();
        }
    };

    const renderModals = () => {
        return (
            <>
                <CandidateMoveModal
                    isOpen={showMoveModal}
                    onClose={() => setShowMoveModal(false)}
                    moveDetails={moveDetails}
                    templates={templates}
                    currentUser={user}
                    rejectionReasons={rejectionReasonsList}
                    onConfirm={handleConfirmShortlistMove}
                />
            </>
        );
    };

    const handleTogglePortalInvite = async (hasAccess: boolean, existingInviteId?: string, forceEmail?: string, forceClientId?: string) => {
        const targetEmail = forceEmail || entity?.email;
        const targetClientId = forceClientId || crmContactLinkedOrgId(entity);
        
        if (!targetEmail || !targetClientId) {
            alert("Deze contactpersoon mist een geldig e-mailadres of is niet gekoppeld aan een bedrijf.");
            return;
        }

        setIsProcessing(true);
        try {
            if (hasAccess && existingInviteId) {
                if (!confirm(`Weet je zeker dat je de toegang voor ${targetEmail} wilt intrekken?`)) {
                    setIsProcessing(false);
                    return;
                }
                await supabase.from('client_portal_invites').delete().eq('id', existingInviteId);
            } else {
                const { error } = await supabase.from('client_portal_invites').insert([
                    { email: targetEmail.trim(), client_id: targetClientId }
                ]);
                if (error) {
                    if (error.code === '23505') throw new Error('Deze contactpersoon heeft al toegang.');
                    throw error;
                }
            }
            if (forceClientId) {
                loadPortalInvites(forceClientId);
            } else {
                loadPortalInvites();
            }
        } catch (err: any) {
            console.error(err);
            alert(err.message || 'Kon toegang niet wijzigen.');
        } finally {
            setIsProcessing(false);
        }
    };

    const checkDns = async (domain: string) => {
        if (!domain) return;
        setIsCheckingDns(true);
        setDnsStatus(null);
        setDnsMessage('');
        try {
            const res = await fetch(`https://dns.google/resolve?name=${domain}&type=ANY`);
            const data = await res.json();
            
            if (data.Answer && data.Answer.length > 0) {
                // Determine if it actually resolves anywhere (simplistic check for any valid A/CNAME records)
                const hasValidRecord = data.Answer.some((a: any) => a.type === 1 || a.type === 5); // A or CNAME
                if (hasValidRecord) {
                    setDnsStatus('success');
                    setDnsMessage('Domein is succesvol gevalideerd in DNS.');
                } else {
                    setDnsStatus('error');
                    setDnsMessage('DNS records gevonden, maar geen geldige A of CNAME records gekoppeld.');
                }
            } else {
                setDnsStatus('error');
                setDnsMessage('Geen actieve DNS records gevonden. CNAME/A verwijst nog nergens naar.');
            }
        } catch (e) {
            setDnsStatus('error');
            setDnsMessage('Kan DNS niet valideren (Netwerkfout).');
        } finally {
            setIsCheckingDns(false);
        }
    };



    const renderClientportaalTab = () => {
        const hasEmail = Boolean(formData.email);
        const activeInvite = portalInvites.find(inv => inv.email?.toLowerCase() === formData.email?.toLowerCase());
        const linkedCo = contactLinkedCompany(entity);
        const portalOrgId = crmContactLinkedOrgId(entity);
        const formOrgId = crmContactLinkedOrgId(formData);

        return (
            <div className="h-full bg-[#f8fafc] p-4 lg:p-8 overflow-y-auto custom-scrollbar">
                <div className="bg-white rounded-2xl p-6 lg:p-8 border border-slate-200 shadow-sm max-w-2xl mx-auto space-y-8 animate-fadeIn">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <ExternalLink size={24} />
                        </div>
                        <h3 className="text-lg font-black text-slate-900 tracking-tight mb-2">
                            Toegang Clientportaal
                        </h3>
                        <p className="text-xs font-medium text-slate-500 max-w-md mx-auto">
                            Verleen <strong>{formData.first_name} {formData.last_name}</strong> toegang tot het afgeschermde vacature/kandidaat portaal van hun bedrijf.
                        </p>
                    </div>

                    {!hasEmail ? (
                        <div className="bg-amber-50 text-amber-700 p-4 border border-amber-200 rounded-xl flex items-start gap-3 text-xs font-bold">
                            <AlertCircle size={16} className="shrink-0" />
                            <p>Deze contactpersoon heeft geen geregistreerd e-mailadres. Vul eerst een e-mailadres in om toegang te verlenen.</p>
                        </div>
                    ) : linkedCo?.active_clientportal ? (
                        <div className="border border-emerald-100 bg-emerald-50 p-6 rounded-2xl flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-emerald-600 shadow-sm mb-4">
                                <CheckCircle2 size={24} />
                            </div>
                            <h4 className="text-sm font-black text-emerald-900 uppercase tracking-widest mb-2">Toegang via Bedrijf</h4>
                            <p className="text-xs font-medium text-emerald-700 mb-4 max-w-sm">Het gekoppelde bedrijf ({linkedCo.name}) heeft reeds actieve portaaltoegang ingesteld. Hierdoor hebben alle contactpersonen van dit bedrijf automatisch toegang.</p>
                            <div className="px-4 py-2 bg-white rounded-xl text-xs font-bold text-slate-900 shadow-sm border border-emerald-100">
                                Inlog: {formData.email}
                            </div>
                            {onLoginAs && portalOrgId && (
                                <button 
                                    onClick={() => onLoginAs(portalOrgId, linkedCo?.name || 'Klant')}
                                    className="mt-4 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors shadow-sm"
                                >
                                    <ExternalLink size={14} /> Test Inloggen (Als deze Klant)
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="border border-slate-100 bg-slate-50 p-6 rounded-2xl">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">E-mailadres Inlog</div>
                                    <div className="text-sm font-bold text-slate-900">{formData.email}</div>
                                </div>
                                <div className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-xl border ${activeInvite ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                                    {activeInvite ? 'Actief' : 'Geen Toegang'}
                                </div>
                            </div>

                            <button
                                onClick={() => handleTogglePortalInvite(Boolean(activeInvite), activeInvite?.id)}
                                disabled={isProcessing}
                                className={`w-full py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-all disabled:opacity-50 ${activeInvite ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100' : 'bg-slate-900 text-white hover:bg-slate-800 shadow-sm'}`}
                            >
                                {isProcessing ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : activeInvite ? (
                                    <><Trash2 size={16} /> Toegang Intrekken</>
                                ) : (
                                    <><CheckCircle2 size={16} /> Toegang Verlenen</>
                                )}
                            </button>
                            
                            {activeInvite && (
                                <div className="mt-4 flex flex-col gap-2">
                                    {onLoginAs && formOrgId && (
                                        <button 
                                            onClick={() => onLoginAs(formOrgId, linkedCo?.name || 'Klant')}
                                            className="w-full py-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors shadow-sm"
                                        >
                                            <ExternalLink size={12} /> Test Inloggen (Als Klant)
                                        </button>
                                    )}
                                    <p className="text-[10px] text-center text-slate-400">
                                        Toegang verleend op: {new Date(activeInvite.created_at).toLocaleDateString('nl-NL')}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col overflow-hidden animate-slideRight">
            
            {/* COMPACT HEADER */}
                <div className="px-6 py-4 border-b border-slate-100 bg-white z-10 shrink-0 flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-3 flex-1 min-w-0">
                        {/* Prominent Back Button */}
                        <div>
                            <button
                                onClick={onBack}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest text-[#e51b23] bg-red-50 border border-red-100 hover:bg-red-100 hover:shadow-md transition-all shadow-sm"
                            >
                                <ArrowLeft size={16} /> Terug naar overzicht
                            </button>
                        </div>
                        
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white flex items-center justify-center text-2xl font-black text-slate-300 shrink-0 border border-slate-200 shadow-sm overflow-hidden p-2">
                                {entityType === 'company' ? (formData.logo_url ? <img src={formData.logo_url} alt="Logo" className="w-full h-full object-contain" /> : <Building2 size={28} className="text-slate-300"/>) : (formData.first_name?.[0] || 'C')}
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg md:text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-3">
                                    {isNew ? `${entityType === 'company' ? 'Bedrijf Aanmaken' : 'Contactpersoon Aanmaken'}` : (formData.name || `${formData.first_name || ''} ${formData.last_name || ''}`.trim())}
                                    {!isNew && entityType === 'company' && formData.industry && (
                                        <span className="text-[10px] font-black text-[#e51b23] uppercase tracking-widest bg-red-50 border border-red-100 px-2 py-0.5 rounded shadow-sm">{formData.industry}</span>
                                    )}
                                </h2>
                                <p className="text-xs text-slate-500 font-bold truncate flex items-center gap-2 mb-2">
                                    CRM {entityType === 'company' ? 'Bedrijf' : 'Contactpersoon'}
                                    {formData.bullhorn_id && (
                                        <>
                                            <span className="text-slate-300">•</span>
                                            <span className="font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 uppercase tracking-widest text-[9px]">#{formData.bullhorn_id}</span>
                                        </>
                                    )}
                                </p>
                                
                                {/* Compact Contact Badges */}
                                <div className="flex flex-wrap items-center gap-2 text-[10px] md:text-xs mt-1">
                                    <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md border border-slate-100 group">
                                        <Phone size={10} className="text-slate-400" />
                                        <span className="font-mono text-slate-600 font-medium">{formData.phone || 'Geen nr'}</span>
                                        {formData.phone && (
                                            <button onClick={handleInitiateCall} disabled={isCalling} className="ml-2 text-[9px] font-bold text-green-600 hover:text-green-700 uppercase disabled:opacity-50">
                                                {isCalling ? 'Bellen...' : 'Bellen'}
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
                                        <Mail size={10} className="text-slate-400" />
                                        <span className="text-slate-600 font-medium truncate max-w-[150px]">{formData.email || 'Geen e-mail'}</span>
                                        {formData.email && (
                                            <a href={`mailto:${formData.email}`} className="ml-2 text-[9px] font-bold text-red-600 hover:text-red-700 uppercase">Mailen</a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col items-end gap-3 shrink-0">
                        {error && (
                            <div className="px-3 py-1.5 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 text-red-700 text-[10px] font-bold mr-2 mb-2">
                                <AlertCircle size={12} className="shrink-0" />
                                <span className="hidden md:inline">{error}</span>
                            </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                            <button onClick={() => setIsEditing(!isEditing)} className={`p-1.5 rounded-lg transition-colors ${isEditing ? 'bg-amber-50 text-amber-600 border border-amber-200 shadow-sm' : 'text-slate-400 hover:bg-slate-100 hover:text-amber-500'}`} title={isEditing ? 'Bewerken Uitzetten' : 'Bewerken Aan'}>
                                <Edit size={16} />
                            </button>

                            {!isNew && entityType === 'company' && (
                                <button onClick={() => setShowMergeModal(true)} className="p-1.5 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg transition-colors flex items-center justify-center gap-1.5" title="Samenvoegen">
                                    <ArrowRightLeft size={16} />
                                </button>
                            )}
                            {!isNew && (
                                <button onClick={handleInitiateDelete} className="p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors" title="Verwijderen">
                                    <Trash2 size={16} />
                                </button>
                            )}
                            
                            {(formData.email || formData.website) && (
                                <a href={formData.email ? `mailto:${formData.email}` : formData.website} target="_blank" rel="noreferrer" className="p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-500 rounded-lg transition-colors" title={formData.email ? "Stuur e-mail" : "Open Website"}>
                                    {formData.email ? <Mail size={16} /> : <ExternalLink size={16} />}
                                </a>
                            )}
                            {(formData.phone) && (
                                <button onClick={() => window.open(`https://wa.me/${String(formData.phone).replace(/[^0-9+]/g, '')}`, '_blank')} className="p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-500 rounded-lg transition-colors" title="WhatsApp">
                                    <MessageSquare size={16} />
                                </button>
                            )}
                        </div>

                        {(isEditing || isNew) && (
                             <button
                                onClick={handleSubmit}
                                disabled={isProcessing || (entityType === 'company' ? !formData.name : (!formData.first_name || !crmContactLinkedOrgId(formData)))}
                                className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded text-[9px] font-bold uppercase tracking-wider hover:bg-emerald-600 disabled:opacity-50"
                            >
                                {isProcessing ? <><Loader2 size={10} className="animate-spin" /> Opslaan...</> : <><Save size={10} /> Opslaan</>}
                            </button>
                        )}
                    </div>
                </div>

                {/* Main Content Layout */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {showCrmShortlistJourney && (
                        <div className="w-full shrink-0 border-b border-slate-100">
                            <CandidatePipelineStrip
                                applications={clientApplications}
                                interviews={clientInterviews}
                                placements={clientPlacements}
                                stageHistory={clientStageHistory}
                                scope={shortlistScope}
                                loading={isLoadingApps}
                                onOpenShortlistTab={openCrmShortlistFromJourney}
                            />
                        </div>
                    )}
                    {/* Modern Tabs */}
                    <div className="flex flex-wrap border-b border-slate-100 bg-white px-4 md:px-6 gap-x-2 gap-y-1 z-10 shrink-0">
                        {entityType === 'company' ? (
                            companyTabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveCompanyTab(tab.id)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-3 text-[9px] md:text-[10px] font-black uppercase tracking-widest border-b-2 transition-all relative top-[1px] whitespace-nowrap ${activeCompanyTab === tab.id ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                                >
                                    {crmTabLoading(tab.id) && (
                                        <Loader2 size={12} className="animate-spin text-[#e51b23] shrink-0" />
                                    )}
                                    {crmTabLabel(tab.id)}
                                </button>
                            ))
                        ) : (
                            contactTabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveContactTab(tab.id)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-3 text-[9px] md:text-[10px] font-black uppercase tracking-widest border-b-2 transition-all relative top-[1px] whitespace-nowrap ${activeContactTab === tab.id ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                                >
                                    {crmTabLoading(tab.id) && (
                                        <Loader2 size={12} className="animate-spin text-[#e51b23] shrink-0" />
                                    )}
                                    {crmTabLabel(tab.id)}
                                </button>
                            ))
                        )}
                    </div>
                    {/* Content Area */}
                    <div className="flex-1 overflow-hidden flex flex-col min-h-0 p-0 bg-[#F8FAFC] relative">
                        {entityType === 'company' ? renderCompanyTabContent() : renderContactTabContent()}
                    </div>
                </div>
            </div>

            {/* AI Logo Search Modal */}
            {showLogoSearch && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[150] p-4">
                    <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-fadeIn">
                        <div className="flex items-center justify-between p-6 bg-indigo-50 border-b border-indigo-100">
                            <div>
                                <h3 className="text-xl font-black text-indigo-900 tracking-tight flex items-center gap-2">
                                    <Bot className="text-indigo-500" /> AI Logo Suggesties
                                </h3>
                                <p className="text-xs text-indigo-700/70 font-bold uppercase tracking-widest mt-1">Gevonden voor: {formData.name}</p>
                            </div>
                            <button onClick={() => setShowLogoSearch(false)} className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all shadow-sm">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                            {isSearchingLogo ? (
                                <div className="flex flex-col items-center justify-center h-48 gap-4">
                                    <Loader2 size={32} className="animate-spin text-indigo-500" />
                                    <p className="text-sm font-bold text-slate-500">Afbeeldingen zoeken op het web...</p>
                                </div>
                            ) : logoSearchError ? (
                                <div className="bg-red-50 text-red-600 p-6 rounded-2xl border border-red-100 text-sm font-bold flex flex-col items-center justify-center gap-2 text-center h-48">
                                    <AlertCircle size={32} />
                                    {logoSearchError}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                    {logoOptions.map((img, i) => (
                                        <div key={i} onClick={() => handleSelectAILogo(img.url)} className="aspect-square bg-white border-2 border-slate-200 rounded-2xl hover:border-indigo-500 cursor-pointer overflow-hidden p-3 flex items-center justify-center group relative transition-all shadow-sm">
                                            <img 
                                                src={`${(import.meta as any).env.VITE_SERVER_URL || ''}/api/proxy-image?url=${encodeURIComponent(img.url)}`} 
                                                className="w-full h-full object-contain mix-blend-multiply transition-transform group-hover:scale-105" 
                                                alt="Logo Option" 
                                                referrerPolicy="no-referrer"
                                                loading="lazy"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    if (target.parentElement) target.parentElement.style.display = 'none';
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Righthand Contact Detail Slider */}
            {selectedContact && (
                <div className="fixed inset-0 z-[100] flex justify-end">
                    <div 
                        className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm transition-opacity animate-fadeIn"
                        onClick={() => setSelectedContact(null)}
                    />
                    
                    <div className="relative w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-300 translate-x-0 animate-slideInRight">
                        {/* Header */}
                        <div className="p-6 border-b border-slate-100 flex items-start justify-between bg-white sticky top-0 z-10 shrink-0">
                            <div className="flex gap-4 items-center">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black border shadow-sm ${(selectedContact.status || contactForm.status) === 'Active' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                                    {contactForm.first_name ? contactForm.first_name.charAt(0) : '?'}
                                </div>
                                <div>
                                    <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
                                        {contactForm.salutation && <span className="text-slate-400">{contactForm.salutation}</span>}
                                        {contactForm.first_name || 'Nieuw'} {contactForm.last_name || 'Contact'}
                                        <div className={`w-2 h-2 rounded-full ${(selectedContact.status || contactForm.status) === 'Active' ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                    </h2>
                                    <p className="text-sm text-[#e51b23] font-bold uppercase tracking-widest mt-1">
                                        {contactForm.job_title || 'Geen Functie'} {contactForm.department ? ` - ${contactForm.department}` : ''}
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setSelectedContact(null)}
                                className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 text-slate-400 hover:text-slate-900 hover:bg-slate-100 hover:border-slate-300 rounded-full transition-colors shadow-sm"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 overflow-y-auto p-6 bg-[#f8fafc]">
                            {!isEditingContact ? (
                                /* View Mode */
                                <div className="space-y-6">
                                    <div className="flex justify-end gap-2 mb-2">
                                        <button 
                                            onClick={() => setIsEditingContact(true)}
                                            className="px-4 py-2 bg-[#e51b23] text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-sm transition-all hover:-translate-y-0.5"
                                        >
                                            <Edit size={14} /> Bewerken
                                        </button>
                                    </div>

                                    {/* General Info */}
                                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                        <h3 className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] border-b border-red-50 pb-2 flex items-center gap-1.5"><Users size={12} /> Algemene Gegevens</h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Aanhef</span><span className="text-sm font-bold text-slate-700">{contactForm.salutation || '-'}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Volledige Naam</span><span className="text-sm font-bold text-slate-700">{contactForm.first_name} {contactForm.last_name}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Afdeling</span><span className="text-sm font-bold text-slate-700">{contactForm.department || '-'}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Functietitel</span><span className="text-sm font-bold text-slate-700">{contactForm.job_title || '-'}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Status</span><span className="text-sm font-bold text-slate-700">{contactForm.status || 'Active'}</span></div>
                                        </div>
                                    </div>

                                    {/* Contact Details */}
                                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                        <h3 className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] border-b border-red-50 pb-2 flex items-center gap-1.5"><Phone size={12} /> Contactgegevens</h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">E-mail op Werk</span><span className="text-sm font-bold text-slate-700">{contactForm.email || '-'}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Persoonlijke E-mail</span><span className="text-sm font-bold text-slate-700">{contactForm.personal_email || '-'}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Telefoon Werk/Algemeen</span><span className="text-sm font-bold text-slate-700">{contactForm.phone || contactForm.work_phone || '-'}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Mobiele Telefoon</span><span className="text-sm font-bold text-slate-700">{contactForm.mobile_phone || '-'}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Telefoon Thuis</span><span className="text-sm font-bold text-slate-700">{contactForm.home_phone || '-'}</span></div>
                                            <div className="col-span-2"><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Adres</span><span className="text-sm font-bold text-slate-700 whitespace-pre-wrap">{contactForm.address || '-'}</span></div>
                                        </div>
                                    </div>

                                    {/* Extra Info */}
                                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                        <h3 className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] border-b border-red-50 pb-2 flex items-center gap-1.5"><LayoutTemplate size={12} /> Aanvullende Informatie (Labels)</h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Branches</span><span className="text-sm font-bold text-slate-700">{contactForm.branches || '-'}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Categorieën</span><span className="text-sm font-bold text-slate-700">{contactForm.categories || '-'}</span></div>
                                            <div><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Ervaring</span><span className="text-sm font-bold text-slate-700">{contactForm.experience || '-'}</span></div>
                                            <div className="col-span-2"><span className="block text-[9px] font-black uppercase tracking-widest text-slate-400">Algemene Opmerkingen</span><span className="text-sm font-medium text-slate-600 whitespace-pre-wrap">{contactForm.remarks || '-'}</span></div>
                                        </div>
                                    </div>

                                    {/* Clientportaal Toegang (Read Only) */}
                                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                        <h3 className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] border-b border-red-50 pb-2 flex items-center gap-1.5"><Globe size={12}/> Clientportaal Toegang</h3>
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Actieve Toegang</p>
                                                <p className="text-[9px] text-slate-500 font-medium">Bepaalt of deze contactpersoon mag inloggen.</p>
                                            </div>
                                            {contactForm.active_clientportal !== false ? (
                                                <span className="bg-emerald-50 text-emerald-700 font-black uppercase tracking-widest text-[9px] px-2 py-0.5 rounded border border-emerald-200 shadow-sm flex items-center gap-1"><CheckCircle2 size={10}/> Toegestaan</span>
                                            ) : (
                                                <span className="bg-red-50 text-red-600 font-black uppercase tracking-widest text-[9px] px-2 py-0.5 rounded border border-red-200 shadow-sm">Geblokkeerd</span>
                                            )}
                                        </div>
                                        
                                        {contactForm.active_clientportal !== false && onLoginAs && crmContactLinkedOrgId(contactForm) && (
                                            <div className="mt-3">
                                                <button 
                                                    onClick={() => {
                                                        const orgId = crmContactLinkedOrgId(contactForm)!;
                                                        const cName = availableCompanies.find(c => c.id === orgId)?.name || 'Relatie';
                                                        onLoginAs(orgId, cName, contactForm.email?.trim());
                                                    }}
                                                    className="w-full py-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors shadow-sm"
                                                >
                                                    <ExternalLink size={12} /> Test Inloggen (Clientportaal)
                                                </button>
                                            </div>
                                        )}
                                        
                                        <div className="mt-3 pt-3 border-t border-slate-100">
                                            <p className="text-[9px] font-black uppercase text-slate-400 mb-1.5">Algemeen Portaal-link</p>
                                            {partnerDomain ? (
                                                <a href={`https://${partnerDomain}`} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5 truncate bg-slate-50 p-2 rounded-lg border border-slate-200">
                                                    <Globe size={12} className="shrink-0"/> {partnerDomain}
                                                </a>
                                            ) : (
                                                <p className="text-[10px] font-medium text-slate-500 italic bg-slate-50 p-2 rounded-lg">Geen eigen portaaldomein ingesteld door admin.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Edit Mode Form */
                                <div className="space-y-6">
                                    {/* Clientportal Access Block */}
                                    {(() => {
                                        const parentCompany = availableCompanies.find(c => c.id === crmContactLinkedOrgId(contactForm));
                                        const hasPortal = Boolean(parentCompany?.is_partner);
                                        const hasEmail = Boolean(contactForm.email);
                                        const activeInvite = portalInvites.find(inv => inv.email?.toLowerCase() === contactForm.email?.toLowerCase());

                                        if (!hasPortal) return null;

                                        return (
                                            <div className="border border-[#e51b23]/20 bg-red-50/50 p-5 rounded-2xl shadow-sm relative overflow-hidden">
                                                <div className="absolute top-0 right-0 w-32 h-32 bg-[#e51b23]/5 rounded-bl-full -mr-10 -mt-10" />
                                                <div className="flex items-center justify-between mb-3 relative z-10">
                                                    <div className="flex items-center gap-2 text-[#e51b23] font-black text-[12px] uppercase tracking-widest">
                                                        <ExternalLink size={16} /> Toegang Clientportaal
                                                    </div>
                                                    <div className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-xl border ${activeInvite ? 'bg-emerald-50 text-emerald-600 border-emerald-200 shadow-sm' : 'bg-white text-slate-400 border-slate-200'}`}>
                                                        {activeInvite ? 'Actief' : 'Geen Toegang'}
                                                    </div>
                                                </div>
                                                <p className="text-xs text-slate-600 font-medium mb-4 relative z-10">
                                                    Dit bedrijf is geregistreerd als Partner en maakt gebruik van het Clientportaal {parentCompany.custom_domain ? <>op <strong>{parentCompany.custom_domain}</strong></> : <>(<strong>clientportal.werftools.nl</strong>)</>}. Je kunt deze contactpersoon hier toegang toe verlenen.
                                                </p>
                                                {!hasEmail ? (
                                                    <div className="bg-white text-amber-600 p-3 border border-amber-200 rounded-xl flex items-start gap-2 text-[10px] font-bold shadow-sm relative z-10">
                                                        <AlertCircle size={14} className="shrink-0" />
                                                        <p>Vul eerst het 'E-mail op Werk' veld in om toegang te verlenen.</p>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleTogglePortalInvite(Boolean(activeInvite), activeInvite?.id, contactForm.email, crmContactLinkedOrgId(contactForm))}
                                                        disabled={isProcessing || !contactForm.first_name}
                                                        className={`w-full sm:w-auto py-2.5 px-5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-sm disabled:opacity-50 relative z-10 ${activeInvite ? 'bg-white text-[#e51b23] border border-red-200 hover:bg-red-50' : 'bg-[#e51b23] text-white hover:bg-red-700 hover:shadow-md'}`}
                                                    >
                                                        {isProcessing ? (
                                                            <Loader2 size={14} className="animate-spin" />
                                                        ) : activeInvite ? (
                                                            <><Trash2 size={14} /> Toegang Intrekken</>
                                                        ) : (
                                                            <><CheckCircle2 size={14} /> Toegang Verlenen</>
                                                        )}
                                                    </button>
                                                )}
                                                
                                                {activeInvite && (
                                                    <p className="text-[9px] text-slate-400 mt-3 font-bold relative z-10">
                                                        Laatst gewijzigd: {new Date(activeInvite.created_at).toLocaleDateString('nl-NL')}
                                                    </p>
                                                )}

                                                {activeInvite && onLoginAs && crmContactLinkedOrgId(contactForm) && (
                                                    <div className="mt-3 pt-3 border-t border-red-200/50 relative z-10">
                                                        <button 
                                                            onClick={(e) => { e.preventDefault(); onLoginAs(crmContactLinkedOrgId(contactForm)!, parentCompany?.name || 'Klant', contactForm.email?.trim()); }}
                                                            className="w-full sm:w-auto py-2.5 px-5 bg-white text-indigo-600 hover:bg-indigo-50 border border-indigo-200 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors shadow-sm"
                                                        >
                                                            <ExternalLink size={14} /> Test Inlog (Inloggen Als)
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                        <h3 className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] border-b border-red-50 pb-2">Algemeen</h3>
                                        
                                        <div className="mb-4">
                                            <label className="block text-[9px] font-black uppercase tracking-widest text-[#e51b23] mb-1">Bedrijf *</label>
                                            <SearchableCombobox 
                                                options={buildCRMClientComboboxOptions(availableCompanies, entityType, entity)}
                                                value={crmContactLinkedOrgId(contactForm) || (entityType === 'company' ? entity?.id : crmContactLinkedOrgId(entity)) || ''}
                                                onChange={(val) => setContactForm({ ...contactForm, client_id: WERFTOOLS_DEFAULT_TENANT_CLIENT_ID, linked_client_id: val })}
                                                placeholder="Selecteer een bedrijf..."
                                                searchPlaceholder="Zoek bedrijfsnaam..."
                                                icon={Building2}
                                                className="w-full"
                                                isLoading={isLoadingCompanies}
                                                emptyText={
                                                    !isLoadingCompanies && availableCompanies.length === 0
                                                        ? 'Geen bedrijven gevonden.'
                                                        : 'Geen resultaten.'
                                                }
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Aanhef</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.salutation || ''} onChange={e => setContactForm({...contactForm, salutation: e.target.value})} placeholder="Dhr. / Mevr." />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Status</label>
                                                <select className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.status || 'Active'} onChange={e => setContactForm({...contactForm, status: e.target.value})}>
                                                    <option value="Active">Actief (Active)</option>
                                                    <option value="Inactive">Nonactief (Inactive)</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Voornaam</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.first_name || ''} onChange={e => setContactForm({...contactForm, first_name: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Achternaam</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.last_name || ''} onChange={e => setContactForm({...contactForm, last_name: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Afdeling</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.department || ''} onChange={e => setContactForm({...contactForm, department: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Huidige Functietitel</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.job_title || ''} onChange={e => setContactForm({...contactForm, job_title: e.target.value})} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                        <h3 className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] border-b border-red-50 pb-2">Contactgegevens</h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">E-mail op Werk</label>
                                                <input type="email" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.email || ''} onChange={e => setContactForm({...contactForm, email: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Persoonlijke E-mail</label>
                                                <input type="email" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.personal_email || ''} onChange={e => setContactForm({...contactForm, personal_email: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Telefoon Werk</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.phone || contactForm.work_phone || ''} onChange={e => setContactForm({...contactForm, phone: e.target.value, work_phone: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Mobiele Telefoon</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.mobile_phone || ''} onChange={e => setContactForm({...contactForm, mobile_phone: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Telefoon Thuis</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.home_phone || ''} onChange={e => setContactForm({...contactForm, home_phone: e.target.value})} />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Adres</label>
                                                <textarea rows={2} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.address || ''} onChange={e => setContactForm({...contactForm, address: e.target.value})} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                        <h3 className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] border-b border-red-50 pb-2">Overige Informatie</h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Ervaring</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.experience || ''} onChange={e => setContactForm({...contactForm, experience: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Categorieën</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.categories || ''} onChange={e => setContactForm({...contactForm, categories: e.target.value})} />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Branches</label>
                                                <input type="text" className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.branches || ''} onChange={e => setContactForm({...contactForm, branches: e.target.value})} />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Algemene Opmerkingen</label>
                                                <textarea rows={4} className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors" value={contactForm.remarks || ''} onChange={e => setContactForm({...contactForm, remarks: e.target.value})} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Clientportaal Toegang */}
                                    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                        <h3 className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] border-b border-red-50 pb-2 flex items-center gap-1.5"><Globe size={12}/> Clientportaal Toegang</h3>
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Actieve Toegang</p>
                                                <p className="text-[9px] text-slate-500 font-medium">Beheer of deze contactpersoon mag inloggen.</p>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer mt-1">
                                                <input type="checkbox" checked={contactForm.active_clientportal !== false} onChange={e => setContactForm({ ...contactForm, active_clientportal: e.target.checked })} className="sr-only peer" />
                                                <div className="w-8 h-4 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-emerald-500 shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"></div>
                                            </label>
                                        </div>
                                        
                                        <div className="mt-3 pt-3 border-t border-slate-100">
                                            <p className="text-[9px] font-black uppercase text-slate-400 mb-1.5">Algemeen Portaal-link</p>
                                            {partnerDomain ? (
                                                <a href={`https://${partnerDomain}`} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5 truncate bg-slate-50 p-2 rounded-lg border border-slate-200">
                                                    <Globe size={12} className="shrink-0"/> {partnerDomain}
                                                </a>
                                            ) : (
                                                <p className="text-[10px] font-medium text-slate-500 italic bg-slate-50 p-2 rounded-lg">Geen eigen portaaldomein ingesteld door admin.</p>
                                            )}
                                        </div>
                                    </div>

                                    {contactForm.id && (
                                        <div className="pt-8">
                                            <button 
                                                onClick={() => handleDeleteContact(contactForm.id)}
                                                className="w-full py-3 bg-white border border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
                                            >
                                                <Trash2 size={16} /> ALLES WISSEN (Verwijder Persoon)
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer Actions (Only in Edit Mode) */}
                        {isEditingContact && (
                            <div className="p-4 border-t border-slate-100 bg-slate-50 flex gap-3 sticky bottom-0 z-10 shrink-0">
                                <button
                                    onClick={() => contactForm.id ? setIsEditingContact(false) : setSelectedContact(null)}
                                    className="flex-1 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-100 transition-colors"
                                    disabled={isSavingContact}
                                >
                                    Annuleren
                                </button>
                                <button
                                    onClick={handleSaveContact}
                                    disabled={isSavingContact}
                                    className="flex-1 py-3 bg-[#e51b23] text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[#c4151c] transition-colors flex items-center justify-center gap-2 shadow-md shadow-red-500/20"
                                >
                                    {isSavingContact ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Opslaan
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* Delete Confirmation Modal */}
            {deleteConfirmInfo && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md flex flex-col overflow-hidden shadow-2xl animate-fadeInScale border border-red-100">
                        <div className="p-6 bg-red-50 border-b border-red-100 flex items-center justify-between">
                            <h3 className="text-sm font-black text-red-700 uppercase tracking-widest flex items-center gap-2">
                                <AlertCircle size={18} /> Definitief Verwijderen
                            </h3>
                            <button onClick={() => setDeleteConfirmInfo(null)} className="text-red-400 hover:text-red-700 hover:bg-red-100 p-1 rounded-full transition-colors"><X size={16}/></button>
                        </div>
                        <div className="p-6">
                            <p className="text-sm font-medium text-slate-700 mb-4">
                                Weet je zeker dat je {deleteConfirmInfo.type === 'main' ? (entityType === 'company' ? 'dit bedrijf' : 'deze contactpersoon') : 'deze contactpersoon'} definitief wilt uit het CRM wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                            </p>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-[#e51b23] mb-2">
                                {deleteConfirmInfo.type === 'main' && entityType === 'company' ? 'Typ de exacte BEDRIJFSNAAM om te bevestigen:' : 'Typ de exacte VOORNAAM om te bevestigen:'}
                            </label>
                            <input
                                autoFocus
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => {
                                    setDeleteConfirmText(e.target.value);
                                    if (deleteError) setDeleteError(null);
                                }}
                                placeholder="Typ de naam hier..."
                                className={`w-full bg-slate-50 border rounded-xl px-4 py-3 text-sm font-bold text-slate-900 outline-none transition-all mb-2 ${deleteError ? 'border-red-500 focus:ring-1 focus:ring-red-500' : 'border-slate-300 focus:border-red-500 focus:ring-1 focus:ring-red-500'}`}
                            />
                            {deleteError ? (
                                <p className="text-[10px] font-bold text-red-600 mb-4 animate-fadeIn flex items-center gap-1"><AlertCircle size={12}/> {deleteError}</p>
                            ) : (
                                <div className="mb-6"></div>
                            )}
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setDeleteConfirmInfo(null)}
                                    className="flex-1 px-4 py-3 bg-white border border-slate-200 text-slate-600 font-black uppercase tracking-widest text-[10px] rounded-xl hover:bg-slate-50 transition-colors"
                                >
                                    Annuleren
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={!deleteConfirmText.trim() || isProcessing}
                                    className="flex-1 px-4 py-3 bg-[#e51b23] text-white font-black uppercase tracking-widest text-[10px] rounded-xl flex items-center justify-center gap-2 shadow-md shadow-red-500/20 active:scale-95 transition-all disabled:opacity-50 hover:bg-black hover:shadow-black/20"
                                >
                                    {isProcessing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Verwijderen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {showMergeModal && (
                <MergeClientModal 
                    onClose={() => setShowMergeModal(false)}
                    onMergeSuccess={() => { window.location.reload(); }}
                />
            )}
        </>
    );
};

export default CRMDashboard;


